import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= when navigating.
import useShadowNavigate from "@/lib/useShadowNavigate";
// FP3-035F Issue 2 — Detect read-only shadow mode so mutation
// buttons (Start Session, Add Goal, Status menu) hide cleanly.
// FP3-043 Phase A follow-up #9 — Admin-family users who open the
// student workspace WITHOUT a shadow (i.e. via Admin → Students)
// also land in read-only oversight mode. The new hook
// `useStudentWorkspaceReadOnly` covers both paths.
import { useReadOnlyAsProvider, useStudentWorkspaceReadOnly } from "@/components/layout/ReadOnlyProviderBanner";
import AdminStudentOversightBanner from "@/components/layout/AdminStudentOversightBanner";
import PageShell from "@/components/layout/PageShell";
import { ArrowLeft, Target, ChartLineUp, CalendarBlank, TrendUp, TrendDown, Minus, Plus, Play, DotsThreeVertical, GraduationCap, Archive, PencilSimple, CheckCircle, Trash, Warning, CaretRight, Info } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import api, { prettyApiError } from "@/lib/api";
import { localDateStr } from "@/lib/utils";
// FP3-122 §17-22 — Canonical date-only helper. All progress-chart
// x-axis formatters and any user-facing session-date rendering
// must go through this so a stored `2026-08-10` renders as
// `Aug 10` regardless of viewer timezone (was rendering Aug 9 in
// Arizona due to UTC-midnight parsing).
import { formatMonthDay } from "@/lib/dateOnly";
// FP3-122 §17-22 — Canonical goal-average helper. Same math on
// every surface (Overview, Data tab, Progress page) so numbers
// never disagree between views.
import { computeGoalAverage, formatGoalAverage } from "@/lib/goalAverage";
// FP3-125 — Canonical session-participation classifier. One place
// decides Provider Unavailable vs Completed / Absent / Unavailable /
// Cancelled so Tracker, Reports Attendance, and dashboards can never
// disagree.
import { classifySessionParticipation, PARTICIPATION } from "@/lib/sessionParticipation";
// FP3-122 §17-22 — Session-history "Edit Session" trigger opens the
// LogSessionDialog in `mode="edit"` so a mis-documented Held session
// can be corrected to Provider Unavailable (or vice versa) with a
// preserved audit trail.
import LogSessionDialog from "@/components/log/LogSessionDialog";
import ServiceDemographics from "./student/ServiceDemographics";
import { MessageProviderButton } from "@/components/messaging/MessageProviderButton";
import { StudentFidelityCard } from "@/components/fidelity/StudentFidelityCard";
import StudentTeamTab from "@/components/student/StudentTeamTab";
import StudentDocumentsTab from "@/components/student/StudentDocumentsTab";
import StudentAssessmentDocumentsCard from "@/components/student/StudentAssessmentDocumentsCard";
import AssessmentRunsCard from "@/components/student/AssessmentRunsCard";
import StudentGoalPlanTab from "@/components/student/StudentGoalPlanTab";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { PrintDisplayActions } from "@/components/print/PrintDisplayActions";
// FP3-062 — Inline rename dialog for solo provider owners + admin
// family. Surfaced from the H1 header only when the caller is
// permitted to rewrite canonical identity (school-team providers and
// assistants never see it).
import StudentRenameDialog from "@/components/student/StudentRenameDialog";
import { useAuth } from "@/lib/auth";

// Parse a YYYY-MM-DD string as a LOCAL Date (not UTC). new Date("YYYY-MM-DD")
// is parsed as UTC midnight by JS, which renders as the previous day in any
// timezone behind UTC. Full ISO timestamps (with "T") parse correctly as-is.
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return new Date(s);
}

function daysFromNow(dateStr) {
  if (!dateStr) return null;
  const d = parseLocalDate(dateStr);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = parseLocalDate(dateStr);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
}

const trendIcon = (trend) => {
  if (trend === "improving") return <TrendUp size={14} weight="bold" className="text-[#1E3F33]" />;
  if (trend === "declining") return <TrendDown size={14} weight="bold" className="text-[#B85C5C]" />;
  return <Minus size={14} weight="bold" className="text-[#D9A05B]" />;
};

const statusColor = (status) => {
  const map = {
    active: "bg-[#E8F0EB] text-[#1E3F33]",
    draft: "bg-[#EDF2F7] text-[#4A6B8C]",
    achieved: "bg-[#E8F0EB] text-[#1E3F33]",
    discontinued: "bg-[#F3F1EC] text-[#6B7280]",
  };
  return map[status] || "bg-[#F3F1EC] text-[#6B7280]";
};

export default function StudentWorkspace() {
  const { studentId } = useParams();
  const navigate = useShadowNavigate();
  // FP3-043 Phase A follow-up #9 — Two distinct read-only paths:
  //   - `readOnlyShadow` is the original ?as_provider= shadow path
  //     (admin viewing inside a specific provider's workspace).
  //   - `readOnly` is the universal student-workspace read-only
  //     gate: true under EITHER the shadow path OR when an admin
  //     family user enters via Admin → Students (no shadow).
  // Every existing `readOnly.active` check below now flips on for
  // BOTH cases; provider users themselves are never affected.
  const readOnlyShadow = useReadOnlyAsProvider();
  const readOnly = useStudentWorkspaceReadOnly();
  const { user, isSoloProvider, preferences } = useAuth();
  // FP3-075C — Hide the per-student Service Fidelity card entirely
  // when the signed-in user has Fidelity Monitoring turned off. The
  // backend calculator and data are untouched; we just skip the
  // render so the Overview tab stops leading with "behind / pace /
  // commitment" language for users who opted out of fidelity.
  const isFidelityOff = preferences?.fidelity_monitoring === "off";
  // FP3-078F — Also hide in Simple Mode. The card is part of the
  // advanced fidelity surface (calculator, deficit math, "behind
  // pace" language) and Simple Mode users explicitly asked for a
  // quieter Student Overview. Card shows only when ui_mode='advanced'
  // AND fidelity_monitoring='on'.
  const isSimpleMode = preferences?.ui_mode === "simple";
  // FP3-062 — Rename affordance is allowed only for callers whose
  // backend gate (`/api/students/{id}` PATCH) accepts identity edits:
  //   - super_admin / admin / org_admin (admin family)
  //   - solo provider owner (`is_solo_provider` flag from /auth/me —
  //     true when `user.account_type=individual_provider` OR the
  //     org row is marked solo; mirrors `is_solo_provider_user` on
  //     the backend)
  // FP3-062 / FP3-122A / FP3-122B — Rename affordance.
  // FP3-062 / FP3-122A / FP3-122B — Rename affordance gate is
  // declared BELOW `const [student, ...]` because it inspects the
  // loaded student's team_assignments to detect delivering-provider
  // authorization. Do not move it above the student useState — that
  // would trigger a TDZ ReferenceError.
  const [renameOpen, setRenameOpen] = useState(false);
  // FP3-122 — Session correction dialog. `editingSession` carries
  // the full row (attendance/notes/outcome) so LogSessionDialog can
  // seed its state from the existing documentation.
  const [editingSession, setEditingSession] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter 28 — Health Panel deep-link: ?tab=service-demographics opens the
  // Service Commitments tab directly. Falls back to overview otherwise.
  const validTabs = [
    "overview",
    "service-demographics",
    "goals",
    "data",
    "notes",
    "sessions",
    "assessments",
    "tracker",
    // FP3-031 — Shared Student Collaboration Layer.
    "team",
    // FP3-032 — Supporting Documents.
    "documents",
    // FP3-034 — Goal Plan / Skill Map MVP.
    "goal-plan",
  ];
  const requestedTab = searchParams.get("tab");
  const initialTab = validTabs.includes(requestedTab) ? requestedTab : "overview";
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  // FP3-062 / FP3-122A / FP3-122B — Rename affordance gate.
  // Backend `PATCH /students/{id}` is the source of truth. UI mirrors:
  //   - super_admin (any student)
  //   - admin / org_admin (within their org)
  //   - solo provider owner
  //   - a DELIVERING provider whose team_assignments row on THIS
  //     student is CURRENT (not graduated/archived) — FP3-122B.
  //     Assistants are NOT broadened.
  // Observer / shadow / read-only mode still lets admins rename
  // (they retain administrative authority even while viewing
  // read-only); for providers we still respect the read-only lock.
  const canRenameStudent = (() => {
    const role = (user?.permission_role || "").toLowerCase();
    if (["super_admin", "admin", "org_admin"].includes(role)) return true;
    if (readOnly?.active) return false;
    if (role !== "provider") return false;
    // Assistants are `permission_role=provider` + `provider_role_type=assistant`.
    if ((user?.provider_role_type || "").toLowerCase() === "assistant") return false;
    if (isSoloProvider) return true;
    // FP3-122B — Delivering-provider gate. Mirrors
    // `provider_scope.is_active_assignment`: the caller must be on
    // the student's team AND their team_assignments row must not
    // be ended (graduated / archived).
    const callerPid = (user?.provider_id || "").trim();
    if (!callerPid || !student) return false;
    const assigned = student.assigned_providers || [];
    const primary = (student.primary_provider_id || "").trim();
    const onTeam = assigned.includes(callerPid) || primary === callerPid;
    if (!onTeam) return false;
    const teamRow = (student.team_assignments || []).find(
      (r) => r && r.provider_id === callerPid,
    );
    const status = (
      (teamRow && teamRow.assignment_status) || "active"
    ).toLowerCase();
    return !["graduated", "archived"].includes(status);
  })();
  // FP3-032C — Controlled active tab. Without this the `<Tabs>` was
  // uncontrolled (`defaultValue=`), so any remount (e.g. a background
  // student refetch that briefly flipped `loading` to true) reset the
  // active tab back to whatever the URL said at *that* moment. We
  // store the active tab in state, keep it in sync with the URL on
  // mount and on browser back/forward, and update the URL whenever
  // the user picks a different tab so the choice survives a refresh
  // AND any internal remounts during background work (file picker,
  // upload, focus events, etc).
  const [activeTab, setActiveTab] = useState(initialTab);
  // Keep state in sync if the URL changes externally (e.g. a deep
  // link from another page sets `?tab=documents`). We intentionally
  // do NOT push the URL change back into state on every render — the
  // user driving the Tabs is the source of truth once mounted.
  useEffect(() => {
    if (
      requestedTab &&
      validTabs.includes(requestedTab) &&
      requestedTab !== activeTab
    ) {
      setActiveTab(requestedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  const onTabChange = (next) => {
    setActiveTab(next);
    // Mirror the tab choice into the URL so refresh / deep-link / back
    // all keep the same tab. `replace: true` avoids polluting history
    // with one entry per tab click.
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  // FP3-032C — `background` refetches MUST NOT toggle the `loading`
  // skeleton on, because that unmounts the entire `<Tabs>` subtree
  // (including any open dialogs and their internal state — e.g. the
  // Documents upload modal mid-file-pick). The native OS file picker
  // fires a `window.focus` event when it closes, which previously
  // triggered a `setLoading(true)` → Skeleton → modal unmount → user
  // got "kicked out of the Documents tab" before the upload could
  // even start. Background refetches now silently update `student`
  // while keeping the active tab + modal alive.
  const loadStudent = ({ background = false } = {}) => {
    if (!background) setLoading(true);
    // Iter 45s — Pass the provider's local date so backend can filter
    // Session History to past-and-today only (no UTC drift).
    api.getStudent(studentId, { today: localDateStr() })
      .then((r) => setStudent(r.data))
      .catch(console.error)
      .finally(() => {
        if (!background) setLoading(false);
      });
  };

  useEffect(() => {
    loadStudent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // Iter 33d Follow-up 13 — Refetch when the page becomes visible again.
  // Without this, returning to a student page (e.g. after completing a
  // session in /session) shows stale data and the user thinks Session
  // History didn't update. Refresh via the visibilitychange + window
  // focus events covers both tab-switch and SPA back-nav cases.
  // FP3-032C — Always use the `background` mode here so we don't tear
  // down the tab tree while the user is mid-upload (the OS file
  // picker fires a focus event when it closes).
  useEffect(() => {
    const refetch = () => {
      if (document.visibilityState === "visible") {
        loadStudent({ background: true });
      }
    };
    document.addEventListener("visibilitychange", refetch);
    window.addEventListener("focus", refetch);
    return () => {
      document.removeEventListener("visibilitychange", refetch);
      window.removeEventListener("focus", refetch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  if (loading) {
    return (
      <PageShell testId="student-loading">
        <div className="max-w-[1360px] space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </PageShell>
    );
  }

  if (!student) {
    return (
      <PageShell testId="student-not-found">
        <div className="max-w-[1360px]">
          <p className="text-[#6B7280]">Student not found.</p>
        </div>
      </PageShell>
    );
  }

  const activeGoals = student.goals?.filter((g) => g.status === "active") || [];
  const draftGoals = student.goals?.filter((g) => g.status === "draft") || [];
  const achievedGoals = student.goals?.filter((g) => g.status === "achieved") || [];
  const archivedGoals = student.goals?.filter((g) => g.status === "archived") || [];

  // Build chart data per goal from trial runs
  const chartDataByGoal = {};
  student.trial_runs?.forEach((tr) => {
    if (!chartDataByGoal[tr.goal_id]) chartDataByGoal[tr.goal_id] = [];
    chartDataByGoal[tr.goal_id].push({ date: tr.date, accuracy: tr.accuracy });
  });
  Object.keys(chartDataByGoal).forEach((gid) => {
    chartDataByGoal[gid].sort((a, b) => a.date.localeCompare(b.date));
  });

  // ── Combined Latest Progress chart data ────────────────────────────────
  // Pivot: rows = unique date; columns = goal_<id>; values = accuracy %.
  // Includes only active+achieved goals (skips draft/archived) so the graph
  // tells a real story. recharts renders one Line per goal_<id> column with
  // its own color so you can read trends at a glance.
  const eligibleGoals = [...activeGoals, ...achievedGoals];
  const goalColors = ["#1E3F33", "#4A6B8C", "#D9A05B", "#8FA998", "#B85C5C", "#5C7088"];
  const combinedChart = (() => {
    const byDate = {};
    eligibleGoals.forEach((g, idx) => {
      const runs = chartDataByGoal[g.id] || [];
      runs.forEach(({ date, accuracy }) => {
        if (!byDate[date]) byDate[date] = { date };
        byDate[date][`goal_${g.id}`] = accuracy;
      });
    });
    const rows = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
    return { rows, goals: eligibleGoals };
  })();

  // FP3-035B — Compute age in years;months from DOB. Returns "" if
  // DOB is missing/malformed; the header conditionally hides the
  // "Age" chip when this is empty.
  const studentAgeLabel = (() => {
    const dob = student?.date_of_birth;
    if (!dob || typeof dob !== "string" || dob.length < 10) return "";
    const [by, bm, bd] = dob.slice(0, 10).split("-").map((n) => parseInt(n, 10));
    if (!by || !bm || !bd) return "";
    const now = new Date();
    let years = now.getFullYear() - by;
    let months = now.getMonth() + 1 - bm;
    if (now.getDate() < bd) months -= 1;
    if (months < 0) { years -= 1; months += 12; }
    if (years < 0) return "";
    return `${years};${months}`;
  })();

  return (
    <PageShell testId="student-workspace">
      {/* FP3-112D — Student Workspace-specific bounded container.
          The shared PageShell (FP3-112) grows up to 1600-1760px which
          is right for tables & dashboards, but forms and card layouts
          in this page felt stretched edge-to-edge. Cap at 1360px so
          the workspace stays compact and readable while remaining
          left-aligned near the sidebar (NO mx-auto — content hugs
          the shell edge; extra pixels go to a calm right margin). */}
      <div className="max-w-[1360px]" data-testid="student-workspace-inner">
      {/* FP3-043 Phase A follow-up #9 — Calm "Admin View" banner for
          admin-family users who arrived via Admin → Students. The
          existing shadow banner (mounted in AppLayout) covers the
          ?as_provider= path. */}
      <AdminStudentOversightBanner />

      {/* Back + Header */}
      <button
        data-testid="back-button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1F2937] transition-colors mb-4"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm mb-6">
        <CardContent className="p-6">
          {/* FP3-112D — Header identity block first, then a compact
              action row directly BELOW it. Avoids the old
              `justify-between` pattern that pushed Start Session /
              Add Goal / status menu to the extreme right edge of the
              wide shell. Actions now sit next to (below) the student
              name — always within visual reach. */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight" data-testid="student-name">
                {student.first_name} {student.last_name}
              </h1>
              {canRenameStudent && (
                <button
                  type="button"
                  data-testid="student-rename-trigger"
                  onClick={() => setRenameOpen(true)}
                  title="Correct or update this student's canonical name"
                  aria-label="Edit student name"
                  className="inline-flex items-center gap-1 rounded-full border border-[#1E3F33]/25 bg-white px-2.5 py-1 text-[11px] font-medium text-[#1E3F33] hover:bg-[#E8F0EB] hover:border-[#1E3F33]/50 transition-colors"
                >
                  <PencilSimple size={12} weight="duotone" />
                  Edit name
                </button>
              )}
            </div>
            {student.readable_id && (
              <button
                type="button"
                data-testid="student-readable-id"
                onClick={() => {
                  navigator.clipboard?.writeText(student.readable_id);
                }}
                title="Click to copy"
                className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#6B7280] hover:text-[#1E3F33] bg-[#F3F1EC] hover:bg-[#E8F0EB] px-2 py-0.5 rounded-md border border-[#E5E3DB] transition-colors"
              >
                {student.readable_id}
              </button>
            )}
            <div className="flex items-center gap-3 mt-2 text-sm text-[#6B7280]">
              <span>Grade {student.grade}</span>
              <span>&middot;</span>
              <span>{student.campus}</span>
              {/* FP3-035B — Age in years;months. Renders only when
                  DOB is set so the header stays clean for cohorts
                  that haven't entered it yet. */}
              {studentAgeLabel && (
                <>
                  <span>&middot;</span>
                  <span data-testid="student-age">Age {studentAgeLabel}</span>
                </>
              )}
              <span>&middot;</span>
              <Badge className={`text-[10px] ${student.status === "active" ? "bg-[#E8F0EB] text-[#1E3F33]" : "bg-[#EDF2F7] text-[#4A6B8C]"} hover:bg-opacity-90`}>
                {student.status === "active" ? "IEP" : student.status}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(student.services && student.services.length > 0
                ? student.services
                : (student.service_areas || []).map((a) => ({ key: a, label: a, sub_areas: [] }))
              ).map((svc) => (
                <span
                  key={svc.key}
                  title={svc.sub_areas?.length ? `Includes: ${svc.sub_areas.join(", ")}` : undefined}
                  className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#E1EAE4] text-[#1E3F33]"
                >
                  {svc.label}
                </span>
              ))}
            </div>
            {/* FP3-112D — Action group directly below the metadata,
                left-aligned. No `justify-between`, no `ml-auto`. */}
            {!readOnly.active && (
              <div
                className="mt-4 flex items-center gap-2 flex-wrap"
                data-testid="student-header-actions"
              >
                <Button
                  data-testid="start-session-btn"
                  className="h-9 bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-sm"
                  onClick={() => navigate("/session")}
                >
                  <Play size={16} weight="duotone" className="mr-1.5" /> Start Session
                </Button>
                <Button
                  data-testid="add-goal-btn"
                  variant="outline"
                  className="h-9 border-[#E5E3DB] text-[#1F2937] hover:bg-[#F9F8F6] rounded-lg text-sm"
                  onClick={() => navigate(`/goals/new?studentId=${studentId}`)}
                >
                  <Plus size={16} className="mr-1.5" /> Add Goal
                </Button>
                <StudentStatusMenu student={student} onChanged={loadStudent} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs — FP3-032C controlled value so the active tab survives
          any internal remount (background refetches, file picker
          focus events, etc). */}
      <Tabs
        value={activeTab}
        onValueChange={onTabChange}
        data-testid="student-tabs"
      >
        <TabsList className="bg-transparent border-b border-[#E5E3DB] rounded-none w-full justify-start gap-6 h-auto p-0 mb-6">
          {validTabs.map((tab) => (
            <TabsTrigger
              key={tab}
              value={tab}
              data-testid={`tab-${tab}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1E3F33] data-[state=active]:text-[#1E3F33] text-[#6B7280] pb-3 px-0 text-sm font-medium capitalize data-[state=active]:shadow-none"
            >
              {tab === "service-demographics"
                ? "Service Demographics"
                : tab === "team"
                  ? "Team"
                  : tab === "documents"
                    ? "Documents"
                    : tab === "goal-plan"
                      ? "Goal Plan"
                      : tab}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" data-testid="tab-content-overview" className="space-y-6">
          {/* FP3-033 — Print / Display entry points for progress summary. */}
          <div className="flex justify-end -mt-2" data-testid="overview-print-actions">
            <PrintDisplayActions
              printHref={`/print/student/${studentId}/progress-summary`}
              displayHref={`/display/student/${studentId}/progress-summary`}
              label="Progress summary output"
              testIdPrefix="progress"
            />
          </div>
          {/* Iter 45 — Per-student service fidelity: where the provider
              sees at a glance whether they're meeting committed minutes
              for this student this period.

              FP3-075C — Suppressed when the signed-in user has
              Fidelity Monitoring turned off.
              FP3-078F — Also suppressed in Simple Mode (the card is
              part of the advanced fidelity surface). */}
          {student?.assigned_providers?.[0] && !isFidelityOff && !isSimpleMode && (
            <StudentFidelityCard
              studentId={student.id}
              providerId={student.assigned_providers[0]}
            />
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Active Goals Summary */}
              <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
                    <Target size={18} weight="duotone" className="text-[#1E3F33]" /> Active Goals ({activeGoals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3" data-testid="student-overview-active-goals">
                  {/* FP3-122 §1 — Show ALL active goals, not the
                      first 4. The card grows vertically instead of
                      hiding goals; large caseloads with 10+ active
                      goals still render every one.
                      FP3-122 §17-22 — Each goal exposes the canonical
                      Goal Average (mean of per-session percentages)
                      right below the row so the same number surfaces
                      in Overview, Data tab, and Progress. */}
                  {activeGoals.map((goal) => {
                    const avg = computeGoalAverage(
                      goal.id,
                      student.trial_runs,
                      student.goal_evidence,
                    );
                    return (
                      <div key={goal.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#F9F8F6] transition-colors" data-testid={`overview-goal-${goal.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#1F2937] truncate">{goal.goal_text}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E1EAE4] text-[#1E3F33]">{goal.category}</span>
                            <span className="text-xs text-[#6B7280]">{goal.current_mastery}% / {goal.target_mastery}%</span>
                            {trendIcon(goal.trend)}
                            <span
                              data-testid={`overview-goal-average-${goal.id}`}
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#EEF1F8] text-[#3B5FAE] border border-[#3B5FAE]/20"
                              title="Mean of per-session percentages"
                            >
                              Average: {formatGoalAverage(avg)}
                            </span>
                          </div>
                        </div>
                        <div className="w-24">
                          <Progress value={goal.current_mastery} className="h-1.5 bg-[#E5E3DB]" />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Latest Progress — combined view across all eligible goals */}
              {combinedChart.rows.length > 0 && combinedChart.goals.length > 0 && (
                <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="latest-progress-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
                      <ChartLineUp size={18} weight="duotone" className="text-[#4A6B8C]" /> Latest Progress
                    </CardTitle>
                    <p className="text-[11px] text-[#6B7280] mt-1">
                      Combined accuracy across {combinedChart.goals.length} active/achieved goal
                      {combinedChart.goals.length === 1 ? "" : "s"}. For per-goal detail, see the Goals tab.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56" data-testid="combined-progress-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={combinedChart.rows}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: "#6B7280" }}
                            tickFormatter={(v) =>
                              formatMonthDay(v)
                            }
                          />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6B7280" }} />
                          <Tooltip
                            contentStyle={{ borderRadius: 12, border: "1px solid #E5E3DB", fontSize: 12 }}
                            formatter={(val, name) => {
                              const id = name.replace("goal_", "");
                              const g = combinedChart.goals.find((x) => x.id === id);
                              return [`${val}%`, g?.category || "goal"];
                            }}
                            labelFormatter={(v) =>
                              formatMonthDay(v)
                            }
                          />
                          <Legend
                            wrapperStyle={{ fontSize: 11 }}
                            formatter={(name) => {
                              const id = name.replace("goal_", "");
                              const g = combinedChart.goals.find((x) => x.id === id);
                              return g?.category || "goal";
                            }}
                          />
                          {combinedChart.goals.map((g, idx) => (
                            <Line
                              key={g.id}
                              type="monotone"
                              dataKey={`goal_${g.id}`}
                              stroke={goalColors[idx % goalColors.length]}
                              strokeWidth={2}
                              dot={{ r: 2 }}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Service Profile (read-only) */}
              <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="service-profile-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-[#1F2937] heading-font">
                    Service Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[#6B7280] uppercase tracking-wider">Attendance</span>
                    <span
                      data-testid="service-profile-attendance"
                      className="text-xs font-semibold text-[#1F2937] capitalize"
                    >
                      {(student.attendance_pattern || "full_day").replace("_", " ")}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[11px] text-[#6B7280] uppercase tracking-wider">Service commitments</p>
                    {(student.service_obligations || []).length === 0 && (
                      <p className="text-[11px] text-[#9B9890] italic">None on record</p>
                    )}
                    {(student.service_obligations || []).map((ob, i) => {
                      // Display canonical minute-based shape; render legacy gracefully too.
                      if (ob.kind === "no_direct_service") {
                        return (
                          <div
                            key={i}
                            data-testid={`service-obligation-${i}`}
                            className="flex items-center justify-between text-[11px] bg-[#F9F8F6] rounded-md px-2 py-1.5"
                          >
                            <span className="text-[#4B5563] italic">No direct service minutes</span>
                            <span className="text-[10px] text-[#5C7088] capitalize">
                              {ob.service_type || "consult"}
                            </span>
                          </div>
                        );
                      }
                      const isMonthly = ob.kind === "minutes_per_month" || ob.kind === "monthly_minutes";
                      const total = ob.total_minutes ?? (
                        ob.kind === "weekly_sessions"
                          ? (ob.value || 0) * (ob.duration_min || 0)
                          : ob.value || 0
                      );
                      const sessions = ob.delivery?.sessions_per_period ?? ob.value;
                      const minPer = ob.delivery?.minutes_per_session ?? ob.duration_min;
                      return (
                        <div
                          key={i}
                          data-testid={`service-obligation-${i}`}
                          className="flex items-center justify-between text-[11px] bg-[#F9F8F6] rounded-md px-2 py-1.5"
                        >
                          <span className="text-[#1F2937]">
                            <span className="font-semibold tabular-nums">{total}</span>{" "}
                            {isMonthly ? "min/month" : "min/week"}
                            {sessions && minPer && (
                              <span className="text-[#6B7280]">
                                {" "}· {sessions}×{minPer}m
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] text-[#5C7088] capitalize">{ob.service_type}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-[#9B9890] italic">
                    Read-only · used by Schedule Wizard
                  </p>
                </CardContent>
              </Card>

              {/* Providers */}
              <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-[#1F2937] heading-font">Providers</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {student.provider_details?.map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#E8F0EB] flex items-center justify-center text-[10px] font-semibold text-[#1E3F33] heading-font">
                        {p.first_name?.[0]}{p.last_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm text-[#1F2937]">{p.first_name} {p.last_name}</p>
                        <p className="text-[11px] text-[#6B7280]">{p.role}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* IEP/MET Dates */}
              <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-[#1F2937] heading-font flex items-center gap-2">
                    <CalendarBlank size={16} weight="duotone" className="text-[#4A6B8C]" /> Key Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {student.iep_due_date && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#6B7280]">IEP Due</span>
                      <span className={`text-sm font-medium ${daysFromNow(student.iep_due_date) <= 14 ? "text-[#D9A05B]" : "text-[#1F2937]"}`}>
                        {formatDate(student.iep_due_date)}
                      </span>
                    </div>
                  )}
                  {student.met_due_date && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#6B7280]">MET Due</span>
                      <span className="text-sm font-medium text-[#1F2937]">{formatDate(student.met_due_date)}</span>
                    </div>
                  )}
                  {student.sessions?.[0] && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#6B7280]">Last Session</span>
                      <span className="text-sm text-[#1F2937]">{formatDate(student.sessions[0].date)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Iter 27 — Current Schedule (read-only summary of recurring blocks)
                  Iter 30 — also reconciles scheduled minutes against the
                  student's Service Commitments so over/under is visible at
                  a glance. */}
              <CurrentScheduleCard
                blocks={student.recurring_blocks || []}
                serviceObligations={student.service_obligations || []}
                isFidelityOff={isFidelityOff}
              />
            </div>
          </div>
        </TabsContent>

        {/* Service Demographics Tab */}
        <TabsContent value="service-demographics" data-testid="tab-content-service-demographics" className="space-y-6">
          <ServiceDemographics student={student} onSaved={loadStudent} />
        </TabsContent>

        {/* Goals Tab */}
        <TabsContent value="goals" data-testid="tab-content-goals" className="space-y-6">
          {/* FP3-033 — Print / Display entry points. Open in new tab. */}
          <div className="flex justify-end -mt-2" data-testid="goals-print-actions">
            <PrintDisplayActions
              printHref={`/print/student/${studentId}/goals`}
              displayHref={`/display/student/${studentId}/goals`}
              label="Goal sheet output"
              testIdPrefix="goals"
            />
          </div>
          {[
            { title: "Active Goals", goals: activeGoals, key: "active" },
            { title: "Draft Goals", goals: draftGoals, key: "draft" },
            { title: "Achieved Goals", goals: achievedGoals, key: "achieved" },
            { title: "Archived Goals", goals: archivedGoals, key: "archived" },
          ].map(({ title, goals: goalList, key }) => goalList.length > 0 && (
            <div key={key} data-testid={`goals-section-${key}`}>
              <h3 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wider mb-3 heading-font">{title} ({goalList.length})</h3>
              <div className="space-y-3">
                {goalList.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    student={student}
                    chartData={chartDataByGoal[goal.id]}
                    onChanged={loadStudent}
                  />
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Data Tab */}
        <TabsContent value="data" data-testid="tab-content-data" className="space-y-4">
          {/* FP3-122 §17-22 — Per-goal average summary. Renders one
              summary row per goal that has data on record so
              reviewers can read the canonical average without
              scrolling through every dated row. Same math as the
              Overview + Progress page. Uses the SAME
              trial_runs/goal_evidence arrays that the individual
              rows below render from, so the summary can never
              disagree with the underlying data. */}
          {(student.goals || []).length > 0 && (
            <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="data-tab-goal-averages">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#1F2937] heading-font">
                  Goal averages
                </CardTitle>
                <p className="text-[11px] text-[#6B7280] mt-0.5">
                  Mean of per-session percentages. Each targeted session counts once, no matter how many rows it produced.
                </p>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {(() => {
                  const summaries = (student.goals || [])
                    .filter((g) => g.status !== "archived")
                    .map((g) => {
                      const avg = computeGoalAverage(
                        g.id,
                        student.trial_runs,
                        student.goal_evidence,
                      );
                      return { goal: g, avg };
                    })
                    .filter(({ avg }) => avg.sessionCount > 0);
                  if (summaries.length === 0) {
                    return (
                      <p className="text-xs text-[#9B9890] italic" data-testid="data-tab-goal-averages-empty">
                        No targeted-session data yet — the average will populate once at least one goal has a percentage on record.
                      </p>
                    );
                  }
                  return summaries.map(({ goal, avg }) => (
                    <div
                      key={goal.id}
                      data-testid={`data-tab-goal-average-${goal.id}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-[#E5E3DB] bg-[#FBFAF6] px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-[#1F2937] truncate">
                          <span className="font-medium capitalize">{goal.category || "goal"}</span>
                          <span className="text-[#6B7280]"> — {goal.goal_text}</span>
                        </p>
                      </div>
                      <div className="text-[11px] font-semibold tabular-nums text-[#1E3F33] whitespace-nowrap">
                        Average: {formatGoalAverage(avg)}
                      </div>
                    </div>
                  ));
                })()}
              </CardContent>
            </Card>
          )}

          {/* Iter 45t — Combined data view: live trial_runs AND goal_evidence
              rendered together, sorted most-recent first. Trial runs come
              from Live Data Collection; evidence rows come from Add
              Progress Entry / Log Session / Document Session. Each row
              carries a clear chip so chartable vs narrative is obvious.
              Narrative-only rows MUST NOT pretend to be measured data. */}
          {(() => {
            const trialRows = (student.trial_runs || []).map((tr) => ({
              kind: "trial",
              key: `tr-${tr.id}`,
              date: tr.date,
              row: tr,
            }));
            const evidenceRows = (student.goal_evidence || []).map((ev) => ({
              kind: "evidence",
              key: `ev-${ev.id}`,
              date: ev.date,
              row: ev,
            }));
            const merged = [...trialRows, ...evidenceRows].sort((a, b) =>
              (b.date || "").localeCompare(a.date || ""),
            );
            if (merged.length === 0) {
              return (
                <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
                  <CardContent className="p-8 text-center text-[#6B7280] text-sm" data-testid="data-tab-empty">
                    No data on record yet — live trials, manual progress entries, and logged-session evidence will land here.
                  </CardContent>
                </Card>
              );
            }
            return merged.map((item) => {
              if (item.kind === "trial") {
                const tr = item.row;
                const goal = student.goals?.find((g) => g.id === tr.goal_id);
                return (
                  <Card key={item.key} data-testid={`trial-card-${tr.id}`} className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-medium text-[#1F2937]">{formatDate(tr.date)}</p>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E8F0EB] text-[#1E3F33] border border-[#1E3F33]/20">
                              Live Data
                            </span>
                          </div>
                          <p className="text-xs text-[#6B7280] truncate max-w-md">{goal?.goal_text?.slice(0, 80)}...</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-[#1E3F33] heading-font">{tr.accuracy}%</p>
                          <p className="text-[11px] text-[#6B7280]">{tr.correct_count}/{tr.total_trials} correct</p>
                        </div>
                      </div>
                      <div className="flex gap-1 mt-2">
                        {tr.responses?.map((r, i) => (
                          <div
                            key={i}
                            className={`w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center ${
                              r.result === "correct" ? "bg-[#E8F0EB] text-[#1E3F33]"
                              : r.result === "incorrect" ? "bg-[#FDF2F2] text-[#B85C5C]"
                              : r.result === "prompted" ? "bg-[#EDF2F7] text-[#4A6B8C]"
                              : "bg-[#FDF8F3] text-[#D9A05B]"
                            }`}
                            title={r.result}
                          >
                            {r.result === "correct" ? "+" : r.result === "incorrect" ? "-" : r.result === "prompted" ? "P" : "~"}
                          </div>
                        ))}
                      </div>
                      {tr.session_note && (
                        <SessionNoteDisclosure
                          testid={`trial-session-note-${tr.id}`}
                          note={tr.session_note}
                        />
                      )}
                    </CardContent>
                  </Card>
                );
              }
              // evidence row
              const ev = item.row;
              const goal = student.goals?.find((g) => g.id === ev.goal_id);
              const sourceLabel = (
                ev.source === "logged_session" ? "Logged Session"
                : ev.source === "manual_progress_entry" ? "Manual Entry"
                : ev.source === "quick_session" ? "Quick Session"
                : ev.source === "live_session" ? "Live Data"
                : ev.source === "scheduled_session" ? "Scheduled Session"
                : ev.source || "Manual Entry"
              );
              const sourceColor = (
                ev.source === "logged_session" ? "bg-[#EEF1F8] text-[#3B5FAE] border-[#3B5FAE]/20"
                : ev.source === "manual_progress_entry" ? "bg-[#F3F1EC] text-[#1F2937] border-[#E5E3DB]"
                : ev.source === "quick_session" ? "bg-[#FDF8F3] text-[#9C6B2E] border-[#9C6B2E]/20"
                : "bg-[#F3F1EC] text-[#6B7280] border-[#E5E3DB]"
              );
              const entryLabel = (
                ev.entry_type === "narrative_observation" ? "Narrative"
                : ev.entry_type === "percent_trials" ? "Percent / Trials"
                : ev.entry_type === "informal_assessment" ? "Informal Assessment"
                : "Other"
              );
              return (
                <Card
                  key={item.key}
                  data-testid={`evidence-card-${ev.id}`}
                  data-chartable={ev.chartable ? "true" : "false"}
                  className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                          <p className="text-sm font-medium text-[#1F2937]">{formatDate(ev.date)}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${sourceColor}`}>
                            {sourceLabel}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FBFAF6] text-[#6B7280] border border-[#E5E3DB]">
                            {entryLabel}
                          </span>
                          {!ev.chartable && (
                            <span
                              data-testid={`evidence-not-chartable-${ev.id}`}
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F3F1EC] text-[#6B7280] border border-[#E5E3DB]"
                              title="Won't plot on chart"
                            >
                              not chartable
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#6B7280] truncate max-w-md">{goal?.goal_text?.slice(0, 80)}{goal?.goal_text && goal.goal_text.length > 80 ? "..." : ""}</p>
                      </div>
                      {ev.chartable && ev.percent != null && (
                        <div className="text-right shrink-0">
                          <p className="text-lg font-semibold text-[#1E3F33] heading-font">{ev.percent}%</p>
                          {ev.numerator != null && ev.denominator != null && (
                            <p className="text-[11px] text-[#6B7280]">{ev.numerator}/{ev.denominator}</p>
                          )}
                        </div>
                      )}
                    </div>
                    {ev.assessment_label && (
                      <p className="text-[11px] text-[#7E3F8C] mb-1 italic">
                        Probe: {ev.assessment_label}
                      </p>
                    )}
                    {ev.narrative_note && (
                      <p className="text-xs text-[#1F2937] whitespace-pre-wrap leading-relaxed mt-1">
                        {ev.narrative_note}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            });
          })()}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" data-testid="tab-content-notes" className="space-y-3">
          {!readOnly.active && <NoteComposer studentId={studentId} onCreated={loadStudent} />}
          {student.notes?.length === 0 ? (
            <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
              <CardContent className="p-8 text-center text-[#6B7280] text-sm">No notes yet.</CardContent>
            </Card>
          ) : (
            student.notes?.map((note) => (
              <Card key={note.id} data-testid={`note-card-${note.id}`} className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`text-[10px] ${
                      note.type === "progress" ? "bg-[#E8F0EB] text-[#1E3F33]"
                      : note.type === "parent-contact" ? "bg-[#EDF2F7] text-[#4A6B8C]"
                      : note.type === "meeting" ? "bg-[#FDF8F3] text-[#D9A05B]"
                      : "bg-[#F3F1EC] text-[#6B7280]"
                    } hover:bg-opacity-90`}>{note.type}</Badge>
                    <span className="text-xs text-[#6B7280]">{formatDate(note.created_at)}</span>
                  </div>
                  <p className="text-sm text-[#1F2937] leading-relaxed">{note.content}</p>
                  {note.tags?.length > 0 && (
                    <div className="flex gap-1.5 mt-3">
                      {note.tags.map((tag) => (
                        <span key={tag} className="text-[10px] text-[#8FA998] bg-[#E1EAE4] px-2 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Sessions Tab — current schedule + session history */}
        <TabsContent value="sessions" data-testid="tab-content-sessions" className="space-y-4">
          {/* FP3-033 — Print session history entry point. */}
          <div className="flex justify-end -mt-2" data-testid="sessions-print-actions">
            <PrintDisplayActions
              printHref={`/print/student/${studentId}/sessions`}
              label="Session history print"
              testIdPrefix="sessions"
            />
          </div>
          <CurrentScheduleCard
            blocks={student.recurring_blocks || []}
            serviceObligations={student.service_obligations || []}
            isFidelityOff={isFidelityOff}
          />
          <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="session-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
                Session History
              </CardTitle>
              <p className="text-[11px] text-[#6B7280] mt-0.5">
                {student.sessions?.length || 0} session{student.sessions?.length === 1 ? "" : "s"} on record.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {(!student.sessions || student.sessions.length === 0) ? (
                <p className="text-xs text-[#9B9890] italic">No sessions on record yet.</p>
              ) : (
                student.sessions.map((session) => (
                  <div
                    key={session.id}
                    data-testid={`session-history-${session.id}`}
                    className="border border-[#E5E3DB] rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-right min-w-[70px]">
                          <p className="text-sm font-medium text-[#1F2937]">{formatDate(session.date)}</p>
                          <p className="text-xs text-[#6B7280]">{formatTime(session.start_time)} - {formatTime(session.end_time)}</p>
                        </div>
                        <Separator orientation="vertical" className="h-8 bg-[#E5E3DB]" />
                        <div>
                          <Badge variant="outline" className="text-[10px] border-[#E5E3DB] text-[#6B7280]">{session.session_type}</Badge>
                          <Badge className={`ml-2 text-[10px] ${
                            session.status === "completed" ? "bg-[#E8F0EB] text-[#1E3F33]"
                            : session.status === "cancelled" ? "bg-[#FDF2F2] text-[#B85C5C]"
                            : "bg-[#EDF2F7] text-[#4A6B8C]"
                          } hover:bg-opacity-90`}>{session.status}</Badge>
                          {/* Iter 45t — Logged-after-the-fact indicator.
                              Helps providers and reviewers tell at a
                              glance which completed rows came from the
                              calm "Log Session" / "Document Session"
                              workflow vs the live data tracker. */}
                          {session.logged_after_the_fact && (
                            <Badge
                              data-testid={`session-logged-after-${session.id}`}
                              className="ml-2 text-[10px] bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#FDF8F3]"
                            >
                              logged after
                            </Badge>
                          )}
                          {session.is_quick && (
                            <Badge
                              data-testid={`session-quick-${session.id}`}
                              className="ml-2 text-[10px] bg-[#F3F1EC] text-[#6B7280] hover:bg-[#F3F1EC]"
                            >
                              quick
                            </Badge>
                          )}
                          {/* FP3-114A — Session-level Provider
                              Unavailable badge. Replaces the
                              completed/attendance chips for these
                              rows so the surface is unambiguous. */}
                          {session.session_outcome === "provider_unavailable" && (
                            <Badge
                              data-testid={`session-provider-unavailable-${session.id}`}
                              className="ml-2 text-[10px] bg-[#FBF6EC] text-[#5A3E12] border border-[#E7D6B4] hover:bg-[#FBF6EC]"
                            >
                              Provider unavailable
                            </Badge>
                          )}
                          {/* FP3-122 — Corrected-session indicator.
                              Any row that has been re-logged carries
                              a non-empty `correction_history[]`; the
                              chip reassures reviewers that the prior
                              documentation was preserved rather than
                              silently rewritten. */}
                          {Array.isArray(session.correction_history) && session.correction_history.length > 0 && (
                            <Badge
                              data-testid={`session-corrected-${session.id}`}
                              className="ml-2 text-[10px] bg-[#EEF1F8] text-[#3B5FAE] border border-[#3B5FAE]/20 hover:bg-[#EEF1F8]"
                              title={`Corrected ${session.correction_history.length} time${session.correction_history.length === 1 ? "" : "s"} — prior documentation preserved.`}
                            >
                              corrected
                            </Badge>
                          )}
                          {/* Iter 33d Follow-up 11 — Show per-student
                              attendance from the student_attendance map
                              when present, falling back to session-level
                              attendance. A group session can be
                              completed/present at the session level
                              while THIS student was actually absent —
                              their history must reflect their state. */}
                          {session.status === "completed" && session.session_outcome !== "provider_unavailable" && (() => {
                            const perStudent = (session.student_attendance || {})[student?.id];
                            const att = (perStudent || session.attendance || "").toLowerCase();
                            if (!att || att === "present") return null;
                            return (
                              <Badge
                                data-testid={`session-history-attendance-${session.id}`}
                                className={`ml-2 text-[10px] ${
                                  att === "absent"
                                    ? "bg-[#FBEFEC] text-[#B85C5C]"
                                    : "bg-[#FDF8F3] text-[#9C6B2E]"
                                } hover:bg-opacity-90`}
                              >
                                {att}
                              </Badge>
                            );
                          })()}
                        </div>
                      </div>
                      {/* FP3-122 — Edit trigger surfaces on every
                          completed session so a mis-documented row
                          (wrong outcome, wrong attendance, wrong
                          note) can be corrected without breaking the
                          recurring-block link or duplicating rows.
                          Backend archives the prior state into
                          correction_history[] and marks stale
                          evidence superseded. */}
                      {session.status === "completed" && !readOnly.active && (
                        <button
                          type="button"
                          data-testid={`session-edit-btn-${session.id}`}
                          onClick={() => setEditingSession(session)}
                          className="text-[11px] font-medium text-[#1E3F33] border border-[#1E3F33]/30 rounded-full px-2.5 py-1 hover:bg-[#E8F0EB] transition-colors flex items-center gap-1 shrink-0"
                          title="Correct this session's outcome, attendance, or notes"
                        >
                          <PencilSimple size={12} weight="duotone" />
                          Edit
                        </button>
                      )}
                    </div>
                    {session.notes && <p className="text-xs text-[#6B7280] mt-2 pl-[86px]">{session.notes}</p>}
                    {/* FP3-114 — Session History note surface,
                        cleaned up by FP3-128:
                          - "Student note" scoped to THIS student's
                            slot on the session (never leaks into
                            other participants' history) — inline
                            Add / Edit.
                          - "Session note" (formerly "Shared group
                            note", same canonical
                            `session.session_note` field) —
                            rendered READ-ONLY. Existing
                            corrections happen via the full Session
                            correction workflow, not via an inline
                            action. */}
                    <SessionNotesSection
                      session={session}
                      studentId={student?.id}
                      readOnly={readOnly.active}
                      onUpdated={() => loadStudent({ background: true })}
                    />
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assessments Tab */}
        <TabsContent value="assessments" data-testid="tab-content-assessments" className="space-y-3">
          {/* FP3-034B — Assessment-flavored supporting documents
              (evaluations, academic assessments, progress summaries).
              Uses the same `student_documents` collection as the
              Documents tab, so anything uploaded here also surfaces
              there and is selectable by Goal Plan AI. */}
          <StudentAssessmentDocumentsCard studentId={studentId} />

          {/* FP3-035 — Provider-administered assessment runner records.
              Renders alongside the document/artifact card, so providers
              see uploaded reports + their own informal probes in one
              tab. Goal Plan AI reads completed runs as structured
              evidence (see goal_plans.py / ai_suggest). */}
          <AssessmentRunsCard student={student} canEdit={!readOnly.active} />

          {/* Legacy assessments list — kept only for non-empty data so
              we don't double-render the empty state alongside the new
              Assessment Runner. */}
          {student.assessments?.length > 0 &&
            student.assessments.map((a) => (
              <Card key={a.id} data-testid={`assessment-card-${a.id}`} className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-medium text-[#1F2937]">{a.name}</h4>
                      <p className="text-xs text-[#6B7280] mt-0.5">{formatDate(a.date)}</p>
                    </div>
                    <Badge className={`text-[10px] ${
                      a.status === "completed" ? "bg-[#E8F0EB] text-[#1E3F33]"
                      : a.status === "in-progress" ? "bg-[#EDF2F7] text-[#4A6B8C]"
                      : "bg-[#FDF8F3] text-[#D9A05B]"
                    } hover:bg-opacity-90`}>{a.status}</Badge>
                  </div>
                  <Badge variant="outline" className="text-[10px] border-[#E5E3DB] text-[#6B7280] mb-2">{a.type}</Badge>
                  {a.score && <p className="text-sm text-[#1E3F33] font-medium mt-1">{a.score}</p>}
                  {a.notes && <p className="text-xs text-[#6B7280] mt-1">{a.notes}</p>}
                </CardContent>
              </Card>
            ))}
        </TabsContent>

        {/* Iter 33d Follow-up 10 — Tracker tab. Service & attendance fidelity
            for the student. Shows last-30-day attendance counts and per-
            commitment monthly progress (placed minutes vs target, plus
            absent / unavailable minutes). Computed entirely from the
            existing student.sessions + student.recurring_blocks +
            student.service_obligations payload — no new endpoint. */}
        <TabsContent value="tracker" data-testid="tab-content-tracker" className="space-y-4">
          <TrackerTab student={student} isFidelityOff={isFidelityOff} />
        </TabsContent>

        {/* FP3-031 — Shared Student Collaboration Layer. */}
        <TabsContent
          value="team"
          data-testid="tab-content-team"
          className="space-y-4"
        >
          <StudentTeamTab studentId={studentId} />
        </TabsContent>

        {/* FP3-032 — Supporting Documents (Student Evidence Layer). */}
        <TabsContent
          value="documents"
          data-testid="tab-content-documents"
          className="space-y-4"
        >
          <StudentDocumentsTab studentId={studentId} />
        </TabsContent>

        {/* FP3-034 — Goal Plan / Skill Map MVP. */}
        <TabsContent
          value="goal-plan"
          data-testid="tab-content-goal-plan"
          className="space-y-4"
        >
          <StudentGoalPlanTab studentId={studentId} />
        </TabsContent>
      </Tabs>

      {/* FP3-062 — Inline identity rename dialog. Mounted unconditionally
          but only opens when `canRenameStudent` callers tap the pencil
          next to the H1 above. */}
      {canRenameStudent && (
        <StudentRenameDialog
          open={renameOpen}
          onOpenChange={setRenameOpen}
          student={student}
          onSaved={() => loadStudent({ background: true })}
        />
      )}
      {/* FP3-122 — Session correction dialog. When a completed
          session in Session History is tapped Edit, we open
          LogSessionDialog in `edit` mode with the current
          documentation seeded. The backend detects the completed
          state and archives prior attendance/notes/evidence into
          `session.correction_history[]` while marking stale
          goal_evidence + trial_runs `superseded=true`. The
          recurring block link (`_recurring_block_id`) is preserved
          so the schedule is never broken by the correction. */}
      {editingSession && (
        <LogSessionDialog
          open={!!editingSession}
          onOpenChange={(o) => {
            if (!o) setEditingSession(null);
          }}
          mode="edit"
          session={{
            ...editingSession,
            // The dialog reads `students[]` for per-student rows; the
            // session doc only carries `student_ids`. Hydrate from the
            // student payload we already have. Session-history rows
            // sometimes belong to peers (group sessions) — those need
            // their names too so attendance rows read cleanly.
            students: (editingSession.student_ids || []).map((sid) => {
              if (sid === student?.id) {
                return {
                  id: student.id,
                  first_name: student.first_name,
                  last_name: student.last_name,
                };
              }
              // Fallback: student_hydrated on the session row if
              // materializer attached it; otherwise render by id
              // (dialog still functional).
              const hydrated = (editingSession.students || []).find((s) => s.id === sid);
              return hydrated || { id: sid, first_name: "Student", last_name: "" };
            }),
          }}
          onLogged={() => {
            setEditingSession(null);
            loadStudent({ background: true });
          }}
        />
      )}
      </div>
    </PageShell>
  );
}


// ──────────────────────────────────────────────────────────────
// Iter 45o — Collapsed Session Note disclosure. Closed by default,
// opens to reveal the full session-level note attached during Live
// Data Collection. Used on both the Data tab (per trial card) and
// the Sessions tab (per completed session). Keeps the lists calm
// when one session has multiple linked trial cards.
// ──────────────────────────────────────────────────────────────
function SessionNoteDisclosure({ note, testid }) {
  const [open, setOpen] = useState(false);
  if (!note) return null;
  return (
    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] text-[#5C7088] hover:text-[#1E3F33] hover:underline"
      >
        <CaretRight
          size={10}
          weight="bold"
          className={`transition-transform ${open ? "rotate-90" : ""}`}
        />
        {open ? "Hide session note" : "Session note"}
      </button>
      {open && (
        <div
          data-testid={testid}
          className="mt-1.5 p-2.5 rounded-lg bg-[#FAFAF7] border border-[#E5E3DB] text-[12px] text-[#374151] whitespace-pre-wrap leading-relaxed"
        >
          {note}
        </div>
      )}
    </div>
  );
}


/**
 * FP3-114 — Session History note surface.
 *
 * Renders two independent notes for a single session as seen from
 * one participant's viewpoint:
 *   • the student-specific note (only ever this student's slot,
 *     inline-editable)
 *   • the session note (formerly labeled "Shared group note" —
 *     same canonical `session.session_note` field, now RENDERED
 *     read-only per FP3-128 with the inline Add/Edit affordance
 *     removed; corrections happen via the existing session
 *     correction workflow)
 *
 * The student note supports inline editing via
 * `PATCH /api/sessions/{id}/note`. Empty text clears the student
 * slot.
 *
 * Never renders one student's individual note under another
 * student's history — the caller passes the currently-viewed
 * `studentId` and this component only pulls
 * `session.student_notes[studentId]`.
 */
function SessionNotesSection({
  session,
  studentId,
  readOnly,
  onUpdated,
}) {
  const { user } = useAuth();
  const isProviderUnavailable = session?.session_outcome === "provider_unavailable";
  const studentNote = (session?.student_notes || {})[studentId] || "";
  const groupNote = session?.session_note || "";
  const providerNote = session?.provider_unavailable_note || "";

  const role = ((user?.permission_role || user?.role || "") + "").toLowerCase();
  const isAdmin = ["admin", "super_admin", "school_admin", "org_admin", "district_admin"].includes(role);
  const isSessionProvider =
    !!user?.provider_id &&
    !!session?.provider_id &&
    user.provider_id === session.provider_id;
  // FP3-128 — Was `canEditShared` for the removed inline group-note
  // Add/Edit action. Retained only for the Provider Unavailable
  // path below, where the delivering provider / admin can still
  // edit the provider-unavailability explanation inline.
  const canEditProvider = !readOnly && (isAdmin || isSessionProvider);

  // FP3-114A — Provider Unavailable outcome: only ever render the
  // provider-unavailability surface. Never show student notes or the
  // shared group note (they don't exist for these sessions).
  if (isProviderUnavailable) {
    return (
      <div className="mt-2 pl-[86px] space-y-2" onClick={(e) => e.stopPropagation()}>
        <p
          data-testid={`session-provider-unavailable-explain-${session.id}`}
          className="text-[11px] text-[#5A3E12] italic"
        >
          Session was not provided because the provider was unavailable.
          <span className="ml-2 text-[#6B7280] not-italic">0 delivered minutes</span>
        </p>
        <SessionNoteRow
          session={session}
          target="provider_note"
          studentId={null}
          label="Provider note"
          emptyCta={canEditProvider ? "Add provider note" : null}
          editButtonCopy="Edit provider note"
          editable={canEditProvider}
          text={providerNote}
          testid={`provider-note-${session.id}`}
          onUpdated={onUpdated}
        />
      </div>
    );
  }

  return (
    <div className="mt-2 pl-[86px] space-y-2" onClick={(e) => e.stopPropagation()}>
      <SessionNoteRow
        session={session}
        target="student"
        studentId={studentId}
        label="Student note"
        emptyCta="Add note"
        editable={!readOnly}
        text={studentNote}
        testid={`student-note-${session.id}`}
        onUpdated={onUpdated}
      />
      {/* FP3-128 — The former "Shared group note" surface is now
          the read-only Session Note. Same canonical
          `session.session_note` field (populated by Live Session
          and by legitimate session corrections). The inline
          Add/Edit affordance is removed per FP3-128 directive E;
          corrections continue via the existing full Session
          correction workflow (SessionEditDialog). Empty state is
          omitted so the card doesn't stack two adjacent italic
          placeholders. */}
      {(() => {
        const hasSessionNote = !!groupNote.trim();
        if (!hasSessionNote) return null;
        return (
          <div
            data-testid={`session-note-${session.id}-row`}
            className="space-y-1"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium">
                Session note
              </span>
            </div>
            <div
              data-testid={`session-note-${session.id}`}
              className="p-2.5 rounded-lg bg-[#FAFAF7] border border-[#E5E3DB] text-[12px] text-[#374151] whitespace-pre-wrap leading-relaxed"
            >
              {groupNote}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


function SessionNoteRow({
  session,
  target,
  studentId,
  label,
  emptyCta,
  editButtonCopy,
  editable,
  text,
  testid,
  onUpdated,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(text || "");
  }, [text, editing]);

  const hasText = !!(text || "").trim();
  if (!hasText && !editable) return null;

  const startEdit = () => {
    setDraft(text || "");
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft(text || "");
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { target, text: draft };
      if (target === "student") payload.student_id = studentId;
      await api.updateSessionNote(session.id, payload);
      const successCopy =
        target === "student"
          ? "Student note saved"
          : target === "provider_note"
            ? "Provider note saved"
            : "Shared note saved";
      toast.success(successCopy);
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.detail || err?.message || "Could not save note";
      if (status === 403 && target === "group") {
        msg = "Only the delivering provider or an admin can edit the shared group note.";
      } else if (status === 403 && target === "provider_note") {
        msg = "Only the delivering provider or an admin can edit this provider note.";
      } else if (status === 403 && target === "student") {
        msg = "You do not have permission to edit this student's note.";
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const editCopy = hasText ? (editButtonCopy || "Edit note") : (emptyCta || "Add note");

  return (
    <div data-testid={`${testid}-row`} className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium">
          {label}
        </span>
        {!editing && editable && (
          <button
            type="button"
            data-testid={`${testid}-edit`}
            onClick={startEdit}
            className="text-[11px] font-medium text-[#1E3F33] hover:underline"
          >
            {editCopy}
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-1.5">
          <textarea
            data-testid={`${testid}-textarea`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full min-h-[72px] text-[12px] rounded-lg border border-[#E5E3DB] p-2 bg-white focus:outline-none focus:border-[#1E3F33] whitespace-pre-wrap"
            placeholder={
              target === "student"
                ? "Session note for this student"
                : target === "provider_note"
                  ? "Briefly document why the session could not be provided."
                  : "Shared group note"
            }
          />
          {target === "group" && (
            <p className="text-[10px] text-[#9C6B2E]">
              Changing this affects the shared session note visible to every participant.
            </p>
          )}
          {target === "provider_note" && (
            <p className="text-[10px] text-[#9C6B2E]">
              Changing this updates the provider unavailability note shown on every participant&apos;s history.
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={`${testid}-save`}
              onClick={save}
              disabled={saving}
              className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-[#1E3F33] text-white hover:bg-[#173127] disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              data-testid={`${testid}-cancel`}
              onClick={cancelEdit}
              disabled={saving}
              className="px-2.5 py-1 text-[11px] font-medium rounded-md border border-[#E5E3DB] text-[#3F4756] hover:bg-[#F3F1EC]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : hasText ? (
        <div
          data-testid={testid}
          className="p-2.5 rounded-lg bg-[#FAFAF7] border border-[#E5E3DB] text-[12px] text-[#374151] whitespace-pre-wrap leading-relaxed"
        >
          {text}
        </div>
      ) : (
        <p className="text-[11px] italic text-[#9B9890]" data-testid={`${testid}-empty`}>
          No {target === "student"
            ? "student note"
            : target === "provider_note"
              ? "provider note"
              : "shared group note"} yet.
        </p>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Iter 27: Current Schedule card — shows student's recurring blocks at a
// glance (day, time, provider, service type, cadence). Used on Overview
// and Sessions tabs so providers don't need to bounce to the Schedule view.
// ──────────────────────────────────────────────────────────────
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

function fmtTime12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
}

function CurrentScheduleCard({ blocks, serviceObligations = [], isFidelityOff = false }) {
  // Iter 30 — Reconcile scheduled minutes against each commitment so the
  // user can spot over/under-scheduling without bouncing between tabs.
  // Multi-commitment students partition by service_type; single-commitment
  // students count all blocks against the lone commitment.
  // FP3-111B — When the viewer preference `fidelity_monitoring="off"`,
  // we hide the reconciliation summary ("Scheduled X · Commitment Y ·
  // +30 over") but keep the block list intact. The math is still
  // computed so the underlying data is untouched — we just don't
  // render the comparison chrome.
  const reconciliation = computeScheduleReconciliation(blocks, serviceObligations);

  // Group blocks by owning provider so cross-provider blocks (e.g. when a
  // student is in another caseload) are visually clustered. Each section
  // gets a small heading with the provider's name + role so the user
  // immediately knows why the block doesn't appear in their own Schedule
  // View. Single-provider students keep the legacy flat list.
  const blocksByProvider = blocks.reduce((acc, b) => {
    const key = b.provider_id || "_unknown";
    (acc[key] = acc[key] || { name: b.provider_name || "Unknown", role: b.provider_role || "", blocks: [] }).blocks.push(b);
    return acc;
  }, {});
  const providerSections = Object.values(blocksByProvider);
  const isMultiProvider = providerSections.length > 1;

  return (
    <Card
      className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
      data-testid="current-schedule-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
          <CalendarBlank size={16} weight="duotone" className="text-[#4A6B8C]" />
          Current Schedule
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Reconciliation summary — shown when commitments exist AND the
            viewer has Service Fidelity Monitoring ON. FP3-111B hides
            the comparison entirely when Fidelity is Off. */}
        {!isFidelityOff && reconciliation.length > 0 && (
          <div
            data-testid="current-schedule-reconciliation"
            className="mb-3 space-y-1.5"
          >
            {reconciliation.map((r, i) => (
              <ReconciliationRow key={i} row={r} idx={i} />
            ))}
            {reconciliation.some((r) => r.unallocated_minutes > 0) && (
              <p
                data-testid="current-schedule-unallocated"
                className="text-[11px] text-[#D9A05B] italic"
              >
                {(() => {
                  const total = reconciliation
                    .map((r) => r.unallocated_minutes)
                    .find((m) => m > 0) || 0;
                  return `${total} min/wk scheduled outside any commitment service-type`;
                })()}
              </p>
            )}
          </div>
        )}

        {blocks.length === 0 ? (
          <p className="text-xs text-[#9B9890] italic">
            Not currently scheduled. Use the Schedule Builder or Wizard to add blocks.
          </p>
        ) : (
          <div className="space-y-2">
            {providerSections.map((section, sIdx) => (
              <div
                key={section.name + sIdx}
                data-testid={`current-schedule-provider-${sIdx}`}
              >
                {isMultiProvider && (
                  <p
                    className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold pt-1 pb-1 border-t border-[#ECEAE4] first:border-t-0 first:pt-0"
                    data-testid={`current-schedule-provider-name-${sIdx}`}
                  >
                    {section.name}
                    {section.role && (
                      <span className="text-[#9B9890]"> · {section.role}</span>
                    )}
                  </p>
                )}
                <div className="divide-y divide-[#ECEAE4]">
                  {section.blocks.map((b) => {
                    const wom = b.weeks_of_month;
                    const cadence =
                      Array.isArray(wom) && wom.length > 0 && wom.length < 4
                        ? `${wom.length} of 4 weeks`
                        : null;
                    return (
                      <div
                        key={b.id}
                        data-testid={`current-schedule-${b.id}`}
                        className="grid grid-cols-[60px_120px_1fr_auto] gap-3 py-2 items-center text-xs"
                      >
                        <span className="text-[#1E3F33] font-semibold">{DAYS[b.day_of_week] || "—"}</span>
                        <span className="tabular-nums text-[#1F2937]">
                          {fmtTime12(b.start_time)} – {fmtTime12(b.end_time)}
                        </span>
                        <span className="text-[#4B5563] truncate">
                          {!isMultiProvider && (b.provider_name || "—")}
                          {!isMultiProvider && b.service_type && (
                            <span className="text-[#9B9890]"> · {b.service_type}</span>
                          )}
                          {isMultiProvider && (b.service_type || "—")}
                        </span>
                        {cadence ? (
                          <Badge className="bg-[#E1EAE4] text-[#1E3F33] hover:bg-[#E1EAE4] text-[10px]">
                            {cadence}
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-[#9B9890]">weekly</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Per-commitment reconciliation row — green ✓ when within ±5%, amber ⚠
// otherwise. Mirrors the Health-check tolerance so users see consistent
// numbers across both surfaces.
function ReconciliationRow({ row, idx }) {
  // FP3-034D correction — Five status buckets:
  //   "ok"             → scheduled within ±5% of commitment, no exclusions needed
  //   "over"           → scheduled exceeds commitment + tolerance (provider error)
  //   "adjusted_met"   → scheduled below commitment, but every minute of gap is
  //                       explained by valid exclusions → green ✓
  //   "adjusted_under" → exclusions explain part of gap but real under-scheduling
  //                       remains → amber, distinguishes from plain "under"
  //   "under"          → scheduled below commitment, no exclusions explain it
  const matches = row.status === "ok" || row.status === "adjusted_met";
  const Icon = matches ? CheckCircle : Warning;
  const tone = matches ? "text-[#3F7A5C]" : "text-[#D9A05B]";
  const bg = matches ? "bg-[#EFF3F1]" : "bg-[#FDF8F3]";
  const ex = row.exclusions || {};
  const hasExclusions =
    (ex.excluded_weeks || 0) > 0 ||
    (ex.provider_blocks?.length || 0) > 0 ||
    (ex.calendar_dates?.length || 0) > 0;
  return (
    <div
      data-testid={`current-schedule-reconciliation-${idx}`}
      data-status={row.status}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${bg} text-[11px]`}
    >
      <Icon size={12} weight="fill" className={`${tone} shrink-0`} />
      <span className="font-semibold text-[#1F2937] uppercase tracking-wider text-[10px]">
        {row.label}
      </span>
      <span className="text-[#4B5563]">
        Scheduled {row.scheduled_minutes} min/{row.period} · Commitment {row.target_minutes} min/{row.period}
      </span>
      {hasExclusions && (
        <ReconciliationExclusionsTooltip exclusions={ex} idx={idx} />
      )}
      {row.status === "adjusted_met" && (
        <span
          data-testid={`reconciliation-status-${idx}`}
          className="ml-auto font-semibold text-[#3F7A5C]"
        >
          Met after exclusions{" "}
          <span className="text-[#6B7280] font-normal">
            ({row.excused_minutes} excluded)
          </span>
        </span>
      )}
      {row.status === "adjusted_under" && (
        <span
          data-testid={`reconciliation-status-${idx}`}
          className="ml-auto font-semibold text-[#D9A05B]"
        >
          −{row.remaining_gap_minutes} under{" "}
          <span className="text-[#6B7280] font-normal">
            ({row.excused_minutes} excused)
          </span>
        </span>
      )}
      {row.status === "under" && (
        <span
          data-testid={`reconciliation-status-${idx}`}
          className={`ml-auto font-semibold ${tone}`}
        >
          {row.delta} under
        </span>
      )}
      {row.status === "over" && (
        <span
          data-testid={`reconciliation-status-${idx}`}
          className={`ml-auto font-semibold ${tone}`}
        >
          +{row.delta} over
        </span>
      )}
    </div>
  );
}

// FP3-034D — Hover card explaining WHY the scheduled-minutes math
// subtracted weeks from the standard 4-weeks/month total. Provider
// personal blocks (testing weeks, every-Nth-week) and school
// calendar exceptions (holidays, breaks, closures) are listed
// separately so the provider knows exactly what was excluded.
function ReconciliationExclusionsTooltip({ exclusions, idx }) {
  const providerBlocks = exclusions.provider_blocks || [];
  const calendarDates = exclusions.calendar_dates || [];
  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-testid={`current-schedule-exclusions-info-${idx}`}
          aria-label="Why doesn't this match the standard 4-weeks math?"
          className="ml-1 text-[#5C7088] hover:text-[#1E3F33] focus:outline-none"
        >
          <Info size={12} weight="fill" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        className="w-72 p-3 text-[11.5px] leading-snug"
        data-testid={`current-schedule-exclusions-content-${idx}`}
      >
        <div className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#6B7280] mb-1.5">
          Excluded this month
        </div>
        {providerBlocks.length > 0 && (
          <div className="mb-2">
            <div className="text-[11px] font-semibold text-[#1E3F33] mb-0.5">
              Provider blocks ({providerBlocks.length})
            </div>
            <ul className="space-y-0.5">
              {providerBlocks.map((pb) => (
                <li key={pb.id} className="text-[#3F4756]">
                  · {pb.label}{" "}
                  <span className="text-[#6B7280]">({pb.cadence_summary})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {calendarDates.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold text-[#1E3F33] mb-0.5">
              School calendar ({calendarDates.length})
            </div>
            <ul className="space-y-0.5">
              {calendarDates.map((cd) => (
                <li key={cd.date} className="text-[#3F4756]">
                  · {fmtDate(cd.date)}{" "}
                  <span className="text-[#6B7280]">
                    {cd.label ? `— ${cd.label}` : ""}
                    {cd.kind ? ` (${cd.kind})` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {providerBlocks.length === 0 && calendarDates.length === 0 && (
          <div className="text-[#6B7280]">
            No detailed exclusions returned.
          </div>
        )}
        <p className="text-[10.5px] text-[#6B7280] mt-2 italic">
          Excluded weeks aren't counted as missed minutes. If your
          scheduled minutes equal the commitment minus these
          exclusions, the row reads "Met after exclusions."
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}

// Compute scheduled-minutes-vs-commitment per service_type. Mirrors the
// backend health-check logic so the Student page agrees with the Health
// tab even before the user clicks Recheck.
function computeScheduleReconciliation(blocks, commitments) {
  const directs = (commitments || []).filter(
    (c) => c.kind !== "no_direct_service" && c.total_minutes > 0
  );
  if (directs.length === 0) return [];
  const partition = directs.length > 1;

  const minutesOfBlock = (b) => {
    const [sh, sm] = (b.start_time || "0:0").split(":").map(Number);
    const [eh, em] = (b.end_time || "0:0").split(":").map(Number);
    return Math.max(0, eh * 60 + em - (sh * 60 + sm));
  };

  // Total of all blocks (used to surface unallocated minutes when a
  // student has blocks whose service_type matches no commitment).
  // FP3-034D — Honor `effective_weeks_per_cycle` (server-computed,
  // subtracts provider-personal-block weeks when the student has
  // opted in via `follow_provider_block_schedule`). Legacy rows that
  // don't carry the field fall back to the old 4-weeks math.
  const weeksOf = (b) => {
    if (typeof b.effective_weeks_per_cycle === "number") {
      return b.effective_weeks_per_cycle;
    }
    return (b.weeks_of_month && b.weeks_of_month.length) || 4;
  };
  const totalScheduledWeekly = blocks.reduce((sum, b) => {
    if (b.block_kind === "personal") return sum;
    const dur = minutesOfBlock(b);
    return sum + (dur * weeksOf(b)) / 4;
  }, 0);

  let allocatedWeekly = 0;
  const rows = directs.map((c) => {
    const isMonthly = c.kind === "minutes_per_month" || c.kind === "monthly_minutes";
    const period = isMonthly ? "month" : "week";
    const matchType = (c.service_type || "").toLowerCase();
    const matched = blocks.filter((b) => {
      if (b.block_kind === "personal") return false;
      if (!partition || !matchType) return true;
      return (b.service_type || "").toLowerCase() === matchType;
    });
    let scheduled = 0;
    // FP3-034D correction — Track raw excluded minutes so the
    // status discriminator below knows how much of the gap to
    // commitment is explained by valid no-service exclusions vs
    // genuine under-scheduling.
    let raw_exclusion_minutes = 0;
    // FP3-034D — Aggregate exclusions across the matched blocks for
    // this commitment so the info-icon tooltip can explain WHY the
    // scheduled-minutes math reads what it does.
    const exclusionDetails = {
      excluded_weeks: 0,
      provider_blocks: [],
      calendar_dates: [],
    };
    for (const b of matched) {
      if ((b.excluded_weeks_per_cycle || 0) > 0) {
        exclusionDetails.excluded_weeks += b.excluded_weeks_per_cycle;
        const dur = minutesOfBlock(b);
        if (isMonthly) {
          raw_exclusion_minutes += dur * b.excluded_weeks_per_cycle;
        } else {
          // Weekly commitments measure per-week, so amortize the
          // excluded count across a 4-week cycle.
          raw_exclusion_minutes += (dur * b.excluded_weeks_per_cycle) / 4;
        }
      }
      for (const pb of b.excluded_provider_blocks || []) {
        if (!exclusionDetails.provider_blocks.find((x) => x.id === pb.id)) {
          exclusionDetails.provider_blocks.push(pb);
        }
      }
      for (const cd of b.excluded_calendar_dates || []) {
        if (!exclusionDetails.calendar_dates.find((x) => x.date === cd.date)) {
          exclusionDetails.calendar_dates.push(cd);
        }
      }
    }
    if (isMonthly) {
      // Monthly commitments multiply the block duration by the
      // EFFECTIVE running-weeks per 4-week cycle — that's the
      // single calculation point FP3-034D corrects.
      scheduled = matched.reduce((sum, b) => {
        const dur = minutesOfBlock(b);
        return sum + dur * weeksOf(b);
      }, 0);
      // FP3-034D — A monthly commitment is "matched" to its blocks.
      // Add the equivalent weekly minutes to allocatedWeekly so the
      // "unallocated weekly" rollup below doesn't double-count them
      // as orphaned. (Previously caused a stale "23 min/wk scheduled
      // outside any commitment service-type" message even when the
      // monthly cell read "matched".)
      allocatedWeekly += scheduled / 4;
    } else {
      scheduled = matched.reduce((sum, b) => {
        const dur = minutesOfBlock(b);
        return sum + (dur * weeksOf(b)) / 4;
      }, 0);
      allocatedWeekly += scheduled;
    }
    const target = parseInt(c.total_minutes, 10) || 0;
    const tol = target * 0.05;
    // FP3-034D correction — Excused-aware status discriminator.
    //
    // The math previously compared scheduled vs target directly, so a
    // student with 90 min/month commitment + 1 provider block + 1
    // school holiday read "−30 under" even though the provider had
    // scheduled the most they possibly could. The corrected logic:
    //   gap_to_commitment = max(0, target - scheduled)
    //   excused_gap       = min(gap_to_commitment, raw_exclusion_minutes)
    //   remaining_gap     = gap_to_commitment - excused_gap
    // → if remaining_gap is within the 5% tolerance band, the row is
    //   "matched after exclusions" (green).
    // → if there's still real under-scheduling beyond what valid
    //   exclusions explain, we show "−X under (Y excused)" so the
    //   provider knows what part needs attention.
    // Genuine under-scheduling with no exclusions still surfaces
    // exactly as before.
    const gap_to_commitment = Math.max(0, target - scheduled);
    const excused_gap = Math.min(gap_to_commitment, raw_exclusion_minutes);
    const remaining_gap = gap_to_commitment - excused_gap;
    let status = "ok";
    if (scheduled > target + tol) {
      status = "over";
    } else if (Math.abs(scheduled - target) <= tol) {
      status = "ok";
    } else if (remaining_gap <= tol) {
      // Adjusted expectation met — every minute short of commitment
      // is explained by a valid exclusion.
      status = "adjusted_met";
    } else if (excused_gap > 0) {
      // Partially explained by exclusions; provider still has real
      // unscheduled minutes to address.
      status = "adjusted_under";
    } else {
      status = "under";
    }
    return {
      label: c.service_type || `Commitment ${directs.indexOf(c) + 1}`,
      period,
      scheduled_minutes: Math.round(scheduled),
      target_minutes: target,
      delta: Math.round(scheduled - target),
      status,
      excused_minutes: Math.round(excused_gap),
      remaining_gap_minutes: Math.round(remaining_gap),
      raw_exclusion_minutes: Math.round(raw_exclusion_minutes),
      exclusions: exclusionDetails,
    };
  });

  // Unallocated weekly minutes (shown only on the first row to avoid noise
  // when there are multiple commitments).
  if (rows.length > 0) {
    rows[0].unallocated_minutes = Math.max(
      0,
      Math.round(totalScheduledWeekly - allocatedWeekly)
    );
  }
  return rows;
}

// ──────────────────────────────────────────────────────────────
// Status menu — Graduated / Archive / Reactivate via in-app confirm
// FP3-093 — For school/team providers this menu is PROVIDER-SCOPED:
// it only changes the signed-in provider's own service relationship
// (team_assignments[*].assignment_status), never the org-wide
// student record. Solo owners keep the workspace-wide actions
// because they also control the organization.
// ──────────────────────────────────────────────────────────────
function StudentStatusMenu({ student, onChanged }) {
  const { user, provider, isSoloProvider } = useAuth();
  const role = (user?.permission_role || "").toLowerCase();
  const isAssistant =
    (user?.provider_role_type || "").toLowerCase() === "assistant";
  if (role === "provider" && !isSoloProvider) {
    // Assistants may not end the supervising provider's service
    // relationship — no scoped menu either.
    if (isAssistant) return null;
    return (
      <MyServiceStatusMenu
        student={student}
        providerId={provider?.id}
        onChanged={onChanged}
      />
    );
  }
  return (
    <WorkspaceWideStatusMenu
      student={student}
      onChanged={onChanged}
      soloProviderId={isSoloProvider ? provider?.id || "" : ""}
    />
  );
}

// FP3-093 — Provider-scoped caseload actions.
// FP3-093C — the confirm dialog surfaces the linked-assistant cascade
// impact (assistants supervised by ME on THIS student follow my
// relationship-status change, including their schedule blocks).
function MyServiceStatusMenu({ student, providerId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind: "graduated" | "archived" }
  const myRow = (student.team_assignments || []).find(
    (t) => t.provider_id === providerId
  );
  const myStatus = (myRow?.assignment_status || "active").toLowerCase();
  const isEnded = myStatus === "graduated" || myStatus === "archived";
  const blockCountFor = (pid) =>
    (student.recurring_blocks || []).filter(
      (b) => b.provider_id === pid && b.block_kind !== "personal"
    ).length;
  const myBlockCount = blockCountFor(providerId);
  const providerInfoById = {};
  for (const p of student.provider_details || []) {
    providerInfoById[p.id] = {
      name: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
      role: p.assistant_type || p.role || "",
      isAssistant:
        p.provider_role_type === "assistant" ||
        !!p.assistant_type ||
        !!p.is_assistant,
    };
  }
  const isCurrentSt = (st) =>
    !["graduated", "archived"].includes((st || "active").toLowerCase());
  // FP3-093D — canonical classification: legacy rows may carry
  // assistant_assignment=false even though the provider is an
  // assistant (Jackson Harris shape). The backend repairs + cascades
  // them; the dialog must list them too. A row with NO student-level
  // supervisor is shown only when I am the sole current primary (the
  // backend's safe single-candidate inference).
  const rowIsAssistant = (t) =>
    !!t.assistant_assignment || !!providerInfoById[t.provider_id]?.isAssistant;
  const currentPrimaryIds = (student.team_assignments || [])
    .filter((t) => !rowIsAssistant(t) && isCurrentSt(t.assignment_status))
    .map((t) => t.provider_id);
  const linkedToMe = (t) => {
    const sup = (t.supervising_provider_id || "").trim();
    if (sup) return sup === providerId;
    return (
      currentPrimaryIds.length === 1 && currentPrimaryIds[0] === providerId
    );
  };
  const linkedAssistants = (student.team_assignments || [])
    .filter(
      (t) =>
        rowIsAssistant(t) &&
        t.provider_id !== providerId &&
        linkedToMe(t) &&
        isCurrentSt(t.assignment_status)
    )
    .map((t) => ({
      provider_id: t.provider_id,
      name: providerInfoById[t.provider_id]?.name || "Assistant provider",
      role: providerInfoById[t.provider_id]?.role || "",
      blockCount: blockCountFor(t.provider_id),
    }));

  const applyStatus = async (status, endBlocks) => {
    try {
      const r = await api.updateMyAssignment(student.id, {
        assignment_status: status,
        end_future_blocks: !!endBlocks,
      });
      const d = r.data || {};
      toast.success(
        status === "graduated"
          ? "Graduated from your service — other providers are unaffected"
          : status === "archived"
          ? "Archived from your caseload — other providers are unaffected"
          : "Returned to your active caseload"
      );
      const casc = d.cascaded_assistants || [];
      if (casc.length > 0) {
        toast.info(
          `Linked assistant assignment${casc.length === 1 ? "" : "s"} also ${
            status === "graduated" || status === "archived"
              ? status
              : "reactivated"
          }: ${casc.map((c) => c.provider_name).filter(Boolean).join(", ")}`
        );
      }
      if (d.admin_review_needed) {
        toast.info(
          "No active service providers remain — your admin will see a review alert for this student."
        );
      }
      onChanged && onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        `Failed: ${typeof detail === "string" ? detail : e.message}`
      );
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="my-service-status-menu"
        onClick={() => setOpen((o) => !o)}
        className="border border-[#E5E3DB] hover:bg-[#F9F8F6] rounded-lg h-9 px-2.5 text-sm text-[#1F2937] flex items-center gap-1.5"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            data-testid="my-service-status-options"
            className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-[#E5E3DB] rounded-xl shadow-lg overflow-hidden"
          >
            <p className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-wider text-[#9B9890]">
              My service only — other providers unaffected
            </p>
            {myStatus !== "graduated" && (
              <button
                data-testid="my-status-graduate"
                onClick={() => {
                  setOpen(false);
                  setDialog({ kind: "graduated" });
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
              >
                <GraduationCap size={14} weight="duotone" className="text-[#1E3F33]" />
                Graduate from my service
              </button>
            )}
            {myStatus !== "archived" && (
              <button
                data-testid="my-status-archive"
                onClick={() => {
                  setOpen(false);
                  setDialog({ kind: "archived" });
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
              >
                <Archive size={14} weight="duotone" className="text-[#5C7088]" />
                Archive from my caseload
              </button>
            )}
            {isEnded && (
              <button
                data-testid="my-status-reactivate"
                onClick={() => {
                  setOpen(false);
                  applyStatus("active", false);
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
              >
                Return to my active caseload
              </button>
            )}
          </div>
        </>
      )}
      {dialog && (
        <MyServiceConfirmDialog
          kind={dialog.kind}
          student={student}
          blockCount={myBlockCount}
          linkedAssistants={linkedAssistants}
          onCancel={() => setDialog(null)}
          onConfirm={(endBlocks) => {
            setDialog(null);
            applyStatus(dialog.kind, endBlocks);
          }}
        />
      )}
    </div>
  );
}

function MyServiceConfirmDialog({
  kind, student, blockCount, linkedAssistants = [], onCancel, onConfirm,
}) {
  const [endBlocks, setEndBlocks] = useState(true);
  const isGrad = kind === "graduated";
  const assistantBlockTotal = linkedAssistants.reduce(
    (sum, a) => sum + (a.blockCount || 0), 0
  );
  const hasBlocks = blockCount > 0 || assistantBlockTotal > 0;
  return (
    <div
      data-testid={`my-status-confirm-${kind}`}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-[#E5E3DB]">
          <h3 className="text-base font-semibold text-[#1F2937] heading-font">
            {isGrad ? "Graduate from my service?" : "Archive from my caseload?"}
          </h3>
          <p className="text-xs text-[#6B7280] mt-1">
            {student.first_name} {student.last_name} will be{" "}
            {isGrad ? "graduated from your service" : "archived from your caseload"}{" "}
            and leave your active caseload. Other providers' services,
            schedules, and goals are not affected, and the student's
            organization-wide status stays unchanged. Your historical
            goals, sessions, and notes remain on record.
          </p>
        </div>
        {linkedAssistants.length > 0 && (
          <div
            className="px-5 py-3 border-b border-[#F3F1EC]"
            data-testid="my-status-cascade-assistants"
          >
            <p className="text-xs font-medium text-[#1F2937] mb-1">
              Linked assistant assignment
              {linkedAssistants.length === 1 ? "" : "s"} under your
              supervision will also be {isGrad ? "graduated" : "archived"}:
            </p>
            <ul className="space-y-0.5">
              {linkedAssistants.map((a) => (
                <li
                  key={a.provider_id}
                  className="text-xs text-[#4B5563]"
                  data-testid={`my-status-cascade-assistant-${a.provider_id}`}
                >
                  {a.name}
                  {a.role ? ` · ${a.role}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        {hasBlocks && (
          <div className="px-5 py-3 border-b border-[#F3F1EC]">
            <p className="text-xs text-[#9C6B2E] mb-1" data-testid="my-status-blocks-warning">
              Future recurring schedule blocks with this student:
            </p>
            <ul className="text-xs text-[#9C6B2E] mb-2 space-y-0.5">
              {blockCount > 0 && (
                <li data-testid="my-status-blocks-own">
                  You: {blockCount} future recurring block
                  {blockCount === 1 ? "" : "s"}
                </li>
              )}
              {linkedAssistants
                .filter((a) => (a.blockCount || 0) > 0)
                .map((a) => (
                  <li
                    key={a.provider_id}
                    data-testid={`my-status-blocks-assistant-${a.provider_id}`}
                  >
                    {a.name}: {a.blockCount} future recurring block
                    {a.blockCount === 1 ? "" : "s"}
                  </li>
                ))}
            </ul>
            <label className="flex items-start gap-2 text-xs text-[#1F2937] cursor-pointer">
              <input
                type="checkbox"
                data-testid="my-status-end-blocks-checkbox"
                checked={endBlocks}
                onChange={(e) => setEndBlocks(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                End the future scheduled blocks for this student now —
                yours{linkedAssistants.length > 0 ? " and any linked assistant's" : ""}.
                If unchecked, the blocks stay and are flagged for review
                on Schedule Health.
              </span>
            </label>
          </div>
        )}
        <div className="px-5 py-3 border-t border-[#E5E3DB] flex justify-end gap-2 bg-[#FAFAF7] rounded-b-2xl">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="my-status-confirm-cancel"
            className="rounded-lg h-9 text-sm border-[#d4d2cc]"
          >
            Cancel
          </Button>
          <Button
            data-testid="my-status-confirm-ok"
            onClick={() => onConfirm(hasBlocks ? endBlocks : false)}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-9 text-sm"
          >
            {isGrad ? "Graduate from my service" : "Archive from my caseload"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function WorkspaceWideStatusMenu({ student, onChanged, soloProviderId = "" }) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind, reason }

  // FP3-093C/D §4 — a SOLO owner's workspace-wide graduate/archive
  // also ends the linked assistant assignments. Canonical
  // classification handles legacy rows (assistant_assignment=false)
  // and empty supervisors when the owner is the sole current primary.
  // Admin-family viewers pass no soloProviderId.
  const soloIsCurrent = (st) =>
    !["graduated", "archived"].includes((st || "active").toLowerCase());
  const soloProvById = {};
  for (const p of student.provider_details || []) {
    soloProvById[p.id] = p;
  }
  const soloRowIsAssistant = (t) =>
    !!t.assistant_assignment ||
    soloProvById[t.provider_id]?.provider_role_type === "assistant" ||
    !!soloProvById[t.provider_id]?.assistant_type ||
    !!soloProvById[t.provider_id]?.is_assistant;
  const soloCurrentPrimaryIds = (student.team_assignments || [])
    .filter((t) => !soloRowIsAssistant(t) && soloIsCurrent(t.assignment_status))
    .map((t) => t.provider_id);
  const linkedAssistantNames = soloProviderId
    ? (student.team_assignments || [])
        .filter((t) => {
          if (!soloRowIsAssistant(t)) return false;
          if (t.provider_id === soloProviderId) return false;
          if (!soloIsCurrent(t.assignment_status)) return false;
          const sup = (t.supervising_provider_id || "").trim();
          if (sup) return sup === soloProviderId;
          return (
            soloCurrentPrimaryIds.length === 1 &&
            soloCurrentPrimaryIds[0] === soloProviderId
          );
        })
        .map((t) => {
          const p = soloProvById[t.provider_id];
          const nm = p
            ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
            : "Assistant provider";
          const role = p ? p.assistant_type || p.role || "" : "";
          return role ? `${nm} · ${role}` : nm;
        })
    : [];

  const apply = async (patch, msg) => {
    try {
      await api.updateStudentDemographics(student.id, patch);
      toast.success(msg);
      onChanged && onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const errMsg = Array.isArray(detail)
        ? detail.map((x) => x.msg || JSON.stringify(x)).join("; ")
        : typeof detail === "string"
        ? detail
        : e.message;
      toast.error(`Failed: ${errMsg}`);
    }
  };

  const isArchived = !!student.is_archived || student.status === "archived";
  const isGraduated = student.status === "graduated";

  return (
    <div className="relative">
      <button
        data-testid="student-status-menu"
        onClick={() => setOpen((o) => !o)}
        className="border border-[#E5E3DB] hover:bg-[#F9F8F6] rounded-lg h-9 px-2.5 text-sm text-[#1F2937] flex items-center gap-1.5"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            data-testid="student-status-options"
            className="absolute right-0 top-full mt-1 z-20 w-56 bg-white border border-[#E5E3DB] rounded-xl shadow-lg overflow-hidden"
          >
            {!isGraduated && (
              <button
                data-testid="status-graduate"
                onClick={() => {
                  setOpen(false);
                  setDialog({ kind: "graduate" });
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
              >
                <GraduationCap size={14} weight="duotone" className="text-[#1E3F33]" />
                Mark as graduated
              </button>
            )}
            {!isArchived && (
              <button
                data-testid="status-archive"
                onClick={() => {
                  setOpen(false);
                  setDialog({ kind: "archive", reason: "" });
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
              >
                <Archive size={14} weight="duotone" className="text-[#5C7088]" />
                Archive student
              </button>
            )}
            {(isArchived || isGraduated) && (
              <button
                data-testid="status-reactivate"
                onClick={() => {
                  setOpen(false);
                  // Reactivate clears archive flag and resets to "active";
                  // graduation history (graduated_at) stays on record.
                  apply({ status: "active", is_archived: false, archive_reason: "" }, "Reactivated");
                }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
              >
                Reactivate
              </button>
            )}
          </div>
        </>
      )}
      {dialog && (
        <StatusConfirmDialog
          kind={dialog.kind}
          student={student}
          linkedAssistantNames={linkedAssistantNames}
          onCancel={() => setDialog(null)}
          onConfirm={async (extra) => {
            setDialog(null);
            if (dialog.kind === "graduate") {
              // Iter 27 — graduation status; preserve archive flag if already set
              await apply(
                {
                  status: "graduated",
                  graduated_at: localDateStr(),
                },
                "Student marked as graduated"
              );
            } else if (dialog.kind === "archive") {
              // Archive flips is_archived + archive_reason ONLY.
              // FP3-079E-4d — Service Status is preserved; "archived"
              // is never persisted as a status token (the FP3-092
              // status whitelist would reject it with 422).
              await apply(
                {
                  is_archived: true,
                  archive_reason: extra?.reason || "",
                },
                "Student archived"
              );
            }
          }}
        />
      )}
    </div>
  );
}

function StatusConfirmDialog({
  kind, student, linkedAssistantNames = [], onCancel, onConfirm,
}) {
  const [reason, setReason] = useState("");
  return (
    <div
      data-testid={`status-confirm-${kind}`}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-[#E5E3DB]">
          <h3 className="text-base font-semibold text-[#1F2937] heading-font">
            {kind === "graduate" ? "Mark as graduated?" : "Archive student?"}
          </h3>
          <p className="text-xs text-[#6B7280] mt-1">
            {kind === "graduate"
              ? `${student.first_name} ${student.last_name} will be marked as graduated. Their record stays available.`
              : `${student.first_name} ${student.last_name} will be archived. Their record stays accessible from filters.`}
          </p>
        </div>
        {linkedAssistantNames.length > 0 && (
          <div className="px-5 py-2 border-b border-[#F3F1EC]">
            <p className="text-xs text-[#8A6B33]" data-testid="status-cascade-assistants">
              Linked assistant assignment
              {linkedAssistantNames.length === 1 ? "" : "s"} —{" "}
              {linkedAssistantNames.join(", ")} — will also be marked{" "}
              {kind === "graduate" ? "graduated" : "archived"}.
            </p>
          </div>
        )}
        {kind === "archive" && (
          <div className="px-5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">
              Reason (optional)
            </p>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Moved schools, transferred, etc."
              data-testid="status-confirm-reason"
              className="h-9 text-sm"
            />
          </div>
        )}
        <div className="px-5 py-3 border-t border-[#E5E3DB] flex justify-end gap-2 bg-[#FAFAF7] rounded-b-2xl">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="status-confirm-cancel"
            className="rounded-lg h-9 text-sm border-[#d4d2cc]"
          >
            Cancel
          </Button>
          <Button
            data-testid="status-confirm-ok"
            onClick={() => onConfirm({ reason })}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-9 text-sm"
          >
            {kind === "graduate" ? "Mark graduated" : "Archive"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Goal card with edit / mark-achieved / archive / delete actions
// ──────────────────────────────────────────────────────────────
function GoalCard({ goal, student, chartData, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    goal_text: goal.goal_text,
    target_mastery: goal.target_mastery,
    current_mastery: goal.current_mastery,
    cadence: goal.cadence || "weekly",
  });
  const [busy, setBusy] = useState(false);
  // FP3-032D — Calm in-app delete confirmation replaces window.confirm.
  const [pendingDelete, setPendingDelete] = useState(false);

  // Iter 44b — Resolve the provider this goal belongs to so the
  // contextual "Message provider" action on the goal card can target
  // exactly the person owning the work. Falls back gracefully if the
  // student doc doesn't carry provider_details (e.g., partial payloads).
  const goalProvider = (() => {
    if (!goal?.provider_id) return null;
    const details = student?.provider_details || [];
    const hit = details.find((p) => p.id === goal.provider_id);
    if (!hit) return { id: goal.provider_id, name: "Provider" };
    return {
      id: hit.id,
      name: `${hit.first_name || ""} ${hit.last_name || ""}`.trim() || "Provider",
      role: hit.role || "",
    };
  })();

  const studentName = student
    ? `${student.first_name || ""} ${student.last_name || ""}`.trim()
    : "";
  const goalLabel = goal.category || goal.domain || (goal.goal_text || "").slice(0, 60);

  const trendCol = goal.trend === "improving" ? "text-[#1E3F33]"
    : goal.trend === "declining" ? "text-[#B85C5C]" : "text-[#D9A05B]";

  const apply = async (updates, msg) => {
    setBusy(true);
    try {
      await api.updateGoal(goal.id, updates);
      toast.success(msg || "Goal updated");
      setEditing(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(`Failed: ${prettyApiError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const removeGoal = async () => {
    setBusy(true);
    try {
      await api.deleteGoal(goal.id);
      toast.success("Goal deleted");
      setPendingDelete(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(`Failed: ${prettyApiError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const archived = goal.status === "archived";

  // Iter 45o — Make the goal card clickable. Tapping the card body
  // navigates to the detailed goal/progress view (the same view the
  // sidebar Progress flow opens). Inner controls (Message button,
  // three-dot menu) stop propagation themselves so they keep
  // working without triggering the card-level navigation.
  // FP3-035F Issue 2 — Preserve ?as_provider= when an admin is
  // shadowing the provider workspace.
  // FP3-043 Phase A follow-up #9 — Admin-family users entering via
  // Admin → Students must also see goal cards as read-only.
  const navigate = useShadowNavigate();
  const readOnly = useStudentWorkspaceReadOnly();
  const openDetailView = () => {
    navigate(`/progress?student=${goal.student_id}&goal=${goal.id}`);
  };

  return (
    <Card
      data-testid={`goal-card-${goal.id}`}
      role="button"
      tabIndex={0}
      onClick={openDetailView}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDetailView();
        }
      }}
      className={`bg-white rounded-2xl border border-[#E5E3DB] shadow-sm cursor-pointer transition-shadow hover:shadow-md hover:border-[#1E3F33]/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3F33]/40 ${archived ? "opacity-70" : ""}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2 gap-3">
          <Badge className={`${
            goal.status === "active" ? "bg-[#E8F0EB] text-[#1E3F33]"
            : goal.status === "achieved" ? "bg-[#E8F0EB] text-[#1E3F33]"
            : goal.status === "archived" ? "bg-[#F3F1EC] text-[#6B7280]"
            : "bg-[#EDF2F7] text-[#4A6B8C]"
          } text-[10px] capitalize`}>
            {goal.status}
          </Badge>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${trendCol} flex items-center gap-1`}>
              {goal.trend === "improving" && <TrendUp size={12} weight="bold" />}
              {goal.trend === "declining" && <TrendDown size={12} weight="bold" />}
              {goal.trend !== "improving" && goal.trend !== "declining" && <Minus size={12} weight="bold" />}
              {goal.trend || "new"}
            </span>
            {goalProvider?.id && !readOnly.active && (
              <span onClick={(e) => e.stopPropagation()}>
                <MessageProviderButton
                  testid={`goal-message-${goal.id}`}
                  size="xs"
                  variant="outline"
                  provider={goalProvider}
                  context={{
                    kind: "progress_goal",
                    goal_id: goal.id,
                    goal_label: goalLabel,
                    student_id: student?.id,
                    student_name: studentName,
                    student_readable_id: student?.readable_id,
                    ref_url: student?.id ? `/students/${student.id}?tab=goals` : undefined,
                    ref_label: "Goals tab",
                  }}
                />
              </span>
            )}
            {!readOnly.active && (
              <GoalActionsMenu
                goal={goal}
                busy={busy}
                onEdit={() => setEditing(true)}
                onAchieve={() => apply({ status: "achieved" }, "Marked as achieved")}
                onArchive={() => apply({ status: "archived" }, "Goal archived")}
                onUnarchive={() => apply({ status: "active" }, "Goal reactivated")}
                onDelete={() => setPendingDelete(true)}
              />
            )}
          </div>
        </div>
        {editing ? (
          <div
            className="space-y-2 mb-3"
            data-testid={`goal-edit-${goal.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <textarea
              value={draft.goal_text}
              onChange={(e) => setDraft({ ...draft, goal_text: e.target.value })}
              className="w-full text-sm border border-[#d4d2cc] rounded-lg p-2 min-h-[80px]"
              data-testid={`goal-edit-text-${goal.id}`}
            />
            <div className="grid grid-cols-3 gap-2">
              <label className="text-xs text-[#4B5563]">
                Current %
                <Input
                  type="number"
                  value={draft.current_mastery}
                  onChange={(e) =>
                    setDraft({ ...draft, current_mastery: parseInt(e.target.value, 10) || 0 })
                  }
                  className="h-8 text-xs"
                />
              </label>
              <label className="text-xs text-[#4B5563]">
                Target %
                <Input
                  type="number"
                  value={draft.target_mastery}
                  onChange={(e) =>
                    setDraft({ ...draft, target_mastery: parseInt(e.target.value, 10) || 0 })
                  }
                  className="h-8 text-xs"
                />
              </label>
              <label className="text-xs text-[#4B5563]">
                Cadence
                <Input
                  value={draft.cadence}
                  onChange={(e) => setDraft({ ...draft, cadence: e.target.value })}
                  className="h-8 text-xs"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
                className="rounded-lg h-8 text-xs border-[#d4d2cc]"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={busy}
                data-testid={`goal-edit-save-${goal.id}`}
                onClick={() => apply(draft, "Goal saved")}
                className="bg-[#1E3F33] text-white rounded-lg h-8 text-xs"
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#1F2937] mb-3 leading-relaxed">{goal.goal_text}</p>
        )}
        {!editing && (
          <>
            <div className="flex items-center gap-4 mb-3">
              <div className="flex-1">
                <div className="flex justify-between text-xs text-[#6B7280] mb-1">
                  <span>Progress</span>
                  <span>{goal.current_mastery}% / {goal.target_mastery}%</span>
                </div>
                <Progress
                  value={(goal.current_mastery / Math.max(1, goal.target_mastery)) * 100}
                  className="h-2 bg-[#E5E3DB]"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6B7280]">
              <span className="px-2 py-0.5 rounded-full bg-[#E1EAE4] text-[#1E3F33] font-medium">
                {goal.category}
              </span>
              {goal.cadence && <span>Cadence: {goal.cadence}</span>}
              {goal.target_date && (
                <span>
                  Target: {formatDate(goal.target_date)}
                </span>
              )}
              {goal.iep_goal_reference && <span className="text-[#4A6B8C]">{goal.iep_goal_reference}</span>}
            </div>
            {chartData?.length > 1 && !archived && (
              <div className="mt-4 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#6B7280" }}
                      tickFormatter={(v) =>
                        formatMonthDay(v)
                      }
                    />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#6B7280" }} />
                    <Line
                      type="monotone"
                      dataKey="accuracy"
                      stroke="#1E3F33"
                      strokeWidth={2}
                      dot={{ r: 2, fill: "#1E3F33" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </CardContent>
      {/* FP3-032D — Calm in-app delete confirmation, replaces window.confirm. */}
      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={(o) => !busy && setPendingDelete(o)}
        title="Permanently delete this goal?"
        description="This will remove the goal and any associated trial data from this student's record. This cannot be undone."
        confirmLabel="Delete goal"
        destructive
        onConfirm={removeGoal}
        testId={`goal-delete-confirm-${goal.id}`}
      />
    </Card>
  );
}

function GoalActionsMenu({ goal, busy, onEdit, onAchieve, onArchive, onUnarchive, onDelete }) {
  const [open, setOpen] = useState(false);
  // Iter 45o — every action inside this menu must stop propagation so
  // it never bubbles up to the parent goal card's navigation handler.
  const stop = (fn) => (e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    if (typeof fn === "function") fn();
  };
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        data-testid={`goal-actions-${goal.id}`}
        onClick={stop(() => setOpen((o) => !o))}
        disabled={busy}
        className="text-[#6B7280] hover:text-[#1F2937] p-1 rounded"
      >
        <DotsThreeVertical size={16} weight="bold" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={stop(() => setOpen(false))} />
          <div
            data-testid={`goal-actions-menu-${goal.id}`}
            className="absolute right-0 top-full mt-1 z-20 w-44 bg-white border border-[#E5E3DB] rounded-xl shadow-lg overflow-hidden"
          >
            <button
              data-testid={`goal-edit-${goal.id}`}
              onClick={stop(() => { setOpen(false); onEdit(); })}
              className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
            >
              <PencilSimple size={12} weight="duotone" /> Edit
            </button>
            {goal.status !== "achieved" && (
              <button
                data-testid={`goal-achieve-${goal.id}`}
                onClick={stop(() => { setOpen(false); onAchieve(); })}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2 text-[#1E3F33]"
              >
                <CheckCircle size={12} weight="duotone" /> Mark achieved
              </button>
            )}
            {goal.status !== "archived" ? (
              <button
                data-testid={`goal-archive-${goal.id}`}
                onClick={stop(() => { setOpen(false); onArchive(); })}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2 text-[#5C7088]"
              >
                <Archive size={12} weight="duotone" /> Archive
              </button>
            ) : (
              <button
                data-testid={`goal-unarchive-${goal.id}`}
                onClick={stop(() => { setOpen(false); onUnarchive(); })}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[#F3F1EC] flex items-center gap-2"
              >
                Reactivate
              </button>
            )}
            <button
              data-testid={`goal-delete-${goal.id}`}
              onClick={stop(() => { setOpen(false); onDelete(); })}
              className="w-full text-left px-3 py-2 text-xs hover:bg-[#FDF2F2] flex items-center gap-2 text-[#B85C5C]"
            >
              <Trash size={12} /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Note composer for the Notes tab
// ──────────────────────────────────────────────────────────────
function NoteComposer({ studentId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [type, setType] = useState("progress");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!content.trim()) return toast.error("Write something first");
    setSaving(true);
    try {
      await api.createNote({ student_id: studentId, content: content.trim(), type, tags: [] });
      toast.success("Note added");
      setContent("");
      setOpen(false);
      onCreated && onCreated();
    } catch (e) {
      // Pydantic validation errors come back as an array of {loc,msg,...}; coerce
      // to a readable string instead of rendering "[object Object]".
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((x) => x.msg || JSON.stringify(x)).join("; ")
        : typeof detail === "string"
        ? detail
        : e.message;
      toast.error(`Failed to save note: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button
        data-testid="add-note-btn"
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-[#E5E3DB] text-[#1F2937] hover:bg-[#F9F8F6] rounded-lg h-9 text-sm"
      >
        <Plus size={14} className="mr-1.5" />
        Add Note
      </Button>
    );
  }
  return (
    <Card
      data-testid="add-note-form"
      className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
    >
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="note-type"
            className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white"
          >
            <option value="progress">Progress</option>
            <option value="parent-contact">Parent contact</option>
            <option value="meeting">Meeting</option>
            <option value="general">General</option>
          </select>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What happened today?"
          data-testid="note-content"
          className="w-full text-sm border border-[#d4d2cc] rounded-lg p-2 min-h-[90px]"
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setOpen(false); setContent(""); }}
            className="rounded-lg h-8 text-xs border-[#d4d2cc]"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={saving}
            data-testid="note-submit"
            onClick={submit}
            className="bg-[#1E3F33] text-white rounded-lg h-8 text-xs"
          >
            {saving ? "Saving…" : "Save note"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ──────────────────────────────────────────────────────────────
// Iter 33d Follow-up 10 — Service / Attendance Tracker tab.
// Surfaces fidelity at a glance: how many sessions actually happened,
// how many were absent / unavailable, and whether weekly + monthly
// service commitments are being met.
// All data sourced from the student.sessions / .recurring_blocks /
// .service_obligations payload that's already loaded by GET /students/{id}.
// ──────────────────────────────────────────────────────────────

function durationMin(t1, t2) {
  if (!t1 || !t2) return 0;
  const [h1, m1] = t1.split(":").map(Number);
  const [h2, m2] = t2.split(":").map(Number);
  return h2 * 60 + m2 - (h1 * 60 + m1);
}

function TrackerTab({ student, isFidelityOff = false }) {
  const sessions = student?.sessions || [];
  const obligations = student?.service_obligations || [];
  const recurringBlocks = student?.recurring_blocks || [];

  // Iter 33d Follow-up 12 — Date range selector. Default 30 days,
  // also offers 60 / 90 days and a custom range. Custom uses two date
  // inputs and only applies when both are set.
  const [rangePreset, setRangePreset] = useState("30");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const today = new Date();
  // Iter 45u — local-date discipline for the Sessions-tab attendance
  // window. ISO/UTC slicing here would mis-credit late-evening users
  // by one day; localDateStr keeps the cutoff in the provider's day.
  const isoDay = (d) => localDateStr(d);
  const todayIso = isoDay(today);

  let cutoff;
  let endIso = todayIso;
  let rangeLabel;
  if (rangePreset === "custom" && customStart && customEnd) {
    cutoff = customStart;
    endIso = customEnd;
    rangeLabel = `${customStart} → ${customEnd}`;
  } else {
    const days = rangePreset === "custom" ? 30 : Number(rangePreset);
    const back = new Date(today);
    back.setDate(back.getDate() - days);
    cutoff = isoDay(back);
    rangeLabel = `last ${days} days`;
  }

  // ── Attendance counts within the chosen window ─────────────────────────
  const inRange = sessions.filter(
    (s) => s.date && s.date >= cutoff && s.date <= endIso
  );
  // FP3-125 — Canonical session-participation classifier drives every
  // bucket. Provider Unavailable is now its own category — the prior
  // resolver silently rolled PU into Completed because it checked
  // `s.status === "completed"` first and never inspected
  // `session_outcome`. Tiger Brogdon (production Aug 12 PU session)
  // was the reported reproducer.
  const counts = {
    present: 0,
    absent: 0,
    unavailable: 0,
    provider_unavailable: 0,
    scheduled: 0,
    cancelled: 0,
  };
  inRange.forEach((s) => {
    const bucket = classifySessionParticipation(s, student?.id);
    if (bucket === PARTICIPATION.COMPLETED) counts.present += 1;
    else if (bucket === PARTICIPATION.ABSENT) counts.absent += 1;
    else if (bucket === PARTICIPATION.UNAVAILABLE) counts.unavailable += 1;
    else if (bucket === PARTICIPATION.PROVIDER_UNAVAILABLE) counts.provider_unavailable += 1;
    else if (bucket === PARTICIPATION.SCHEDULED) counts.scheduled += 1;
    else if (bucket === PARTICIPATION.CANCELLED) counts.cancelled += 1;
  });
  const totalSeen = counts.present + counts.absent + counts.unavailable;

  // ── Current-month commitment fidelity ──────────────────────────────────
  const monthStart = isoDay(new Date(today.getFullYear(), today.getMonth(), 1));
  const nextMonthStart = isoDay(new Date(today.getFullYear(), today.getMonth() + 1, 1));
  const monthSessions = sessions.filter(
    (s) => s.date && s.date >= monthStart && s.date < nextMonthStart && s.status === "completed"
  );

  // FP3-035B-2 correction — Per-commitment fidelity rows. The
  // obligation taxonomy expanded in FP3-035B-1 follow-up:
  //   • `minutes_per_week` / `minutes_per_month` — direct service.
  //   • `no_direct_service` — consult / monitoring; excluded here.
  // The legacy filter `o.kind === "direct" || !o.kind` evaluated to
  // FALSE for both new kinds, producing the spurious "No direct-
  // service commitments for this student" message even when
  // commitments existed. We now include any obligation that isn't
  // explicitly marked `no_direct_service` AND has a non-zero
  // target.
  const fidelityRows = obligations
    .filter((o) => {
      if (!o) return false;
      if (o.kind === "no_direct_service") return false;
      // Treat any positive total_minutes as a direct-service
      // commitment; pre-FP3-035B-1 records may still carry the
      // legacy `kind === "direct"` shape — accept those too.
      return (Number(o.total_minutes) || 0) > 0;
    })
    .map((o) => {
      // FP3-035B-2 correction — `service_types[]` (multi-area)
      // wins over the legacy singular `service_type` so commitments
      // covering several focus areas at once attribute their
      // sessions correctly.
      const svcTypes = Array.isArray(o.service_types) && o.service_types.length
        ? o.service_types.map((t) => (t || "").toLowerCase())
        : [(o.service_type || "").toLowerCase()];
      const targetTotal = Number(o.total_minutes || 0);
      // FP3-035B-1 follow-up canonical period derives from kind.
      const period = o.kind === "minutes_per_month"
        ? "month"
        : (o.kind === "minutes_per_week"
            ? "week"
            : (o.frequency_period || "month").toLowerCase());

      // Match completed sessions whose service_type intersects this
      // commitment's focus-area set. If the student has only one
      // commitment, attribute all sessions to it.
      const matched = monthSessions.filter((s) => {
        if (obligations.length <= 1) return true;
        const ssvc = (s.service_type || "").toLowerCase();
        return svcTypes.some((sv) => sv === "" || ssvc === sv);
      });

      let presentMin = 0;
      let absentMin = 0;
      let unavailMin = 0;
      matched.forEach((s) => {
        const dur = durationMin(s.start_time, s.end_time);
        const perStudent = (s.student_attendance || {})[student?.id];
        const att = (perStudent || s.attendance || "present").toLowerCase();
        if (att === "absent") absentMin += dur;
        else if (att === "unavailable") unavailMin += dur;
        else presentMin += dur;
      });

      // For weekly cadence, scale target to whatever portion of the
      // month has elapsed. ~4.33 weeks/month.
      const dayOfMonth = today.getDate();
      const periodTarget =
        period === "week"
          ? Math.round((targetTotal * (dayOfMonth / 7)))
          : period === "year"
          ? Math.round(targetTotal / 12)
          : targetTotal;

      const pct = periodTarget > 0 ? Math.min(100, Math.round((presentMin / periodTarget) * 100)) : 0;
      const blocksForSvc = recurringBlocks.filter((b) => {
        if (obligations.length <= 1) return true;
        const bsvc = (b.service_type || "").toLowerCase();
        return svcTypes.some((sv) => sv === "" || bsvc === sv);
      });
      const totalScheduledWeeklyMin = blocksForSvc.reduce(
        (sum, b) => sum + durationMin(b.start_time, b.end_time),
        0
      );

      // Display label — prefer the legacy singular when present,
      // otherwise join the multi-area list so the row reads
      // naturally ("Articulation + Language").
      const displayLabel = o.service_type
        || (Array.isArray(o.service_types) ? o.service_types.join(" + ") : "")
        || "Service";

      // FP3-035B-2 runtime fix — `delivery` is now always an object
      // (`{sessions_per_period, minutes_per_session}`), never a
      // pre-formatted string. Render-time fallback would crash with
      // "Objects are not valid as a React child" so we pre-format
      // it to readable text here. Empty string when both pieces are
      // missing — the JSX hides the chip when the value is falsy.
      const sessionsPer = parseInt(o.delivery?.sessions_per_period, 10) || 0;
      const minutesPer = parseInt(o.delivery?.minutes_per_session, 10) || 0;
      const deliveryLabel = (() => {
        if (typeof o.delivery === "string") return o.delivery;
        if (sessionsPer > 0 && minutesPer > 0) {
          return `${sessionsPer} × ${minutesPer} min`;
        }
        return "";
      })();

      return {
        key: `${svcTypes.join("|")}-${period}-${deliveryLabel}`,
        label: displayLabel,
        period,
        targetTotal,
        periodTarget,
        presentMin,
        absentMin,
        unavailMin,
        pct,
        totalScheduledWeeklyMin,
        delivery: deliveryLabel,
      };
    });

  return (
    <div className="space-y-4" data-testid="tracker-tab">
      {/* Attendance summary card */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="tracker-attendance-card">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
                Attendance · {rangeLabel}
              </CardTitle>
              <p className="text-[11px] text-[#6B7280] mt-0.5">
                {totalSeen} session{totalSeen === 1 ? "" : "s"} on record · {counts.scheduled} still scheduled
              </p>
            </div>
            {/* Iter 33d Follow-up 12 — Range selector */}
            <div className="flex items-center gap-1 shrink-0" data-testid="tracker-range-selector">
              {[
                { v: "30", label: "30d" },
                { v: "60", label: "60d" },
                { v: "90", label: "90d" },
                { v: "custom", label: "Custom" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`tracker-range-${opt.v}`}
                  onClick={() => setRangePreset(opt.v)}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-md border transition-colors ${
                    rangePreset === opt.v
                      ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                      : "bg-white text-[#4B5563] border-[#E5E3DB] hover:bg-[#F3F1EC]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {rangePreset === "custom" && (
            <div className="flex items-center gap-2 mt-2.5" data-testid="tracker-custom-range">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                max={customEnd || todayIso}
                data-testid="tracker-custom-start"
                className="text-[11px] px-2 py-1 border border-[#E5E3DB] rounded-md focus:outline-none focus:ring-1 focus:ring-[#1E3F33]"
              />
              <span className="text-[11px] text-[#6B7280]">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                min={customStart}
                max={todayIso}
                data-testid="tracker-custom-end"
                className="text-[11px] px-2 py-1 border border-[#E5E3DB] rounded-md focus:outline-none focus:ring-1 focus:ring-[#1E3F33]"
              />
              {(!customStart || !customEnd) && (
                <span className="text-[10px] text-[#9C6B2E] italic">
                  Pick both dates to apply
                </span>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <TrackerStat
            label="Completed"
            value={counts.present}
            tone="ok"
            testid="tracker-stat-present"
          />
          <TrackerStat
            label="Absent"
            value={counts.absent}
            tone="warn"
            testid="tracker-stat-absent"
          />
          <TrackerStat
            label="Unavailable"
            value={counts.unavailable}
            tone="muted"
            testid="tracker-stat-unavailable"
          />
          {/* FP3-125 — Provider Unavailable is now its own bucket so
              a PU session (provider was out, student never received
              service) never inflates the Completed count. */}
          <TrackerStat
            label="Provider Unavailable"
            value={counts.provider_unavailable}
            tone="amber"
            testid="tracker-stat-provider-unavailable"
          />
          <TrackerStat
            label="Cancelled"
            value={counts.cancelled}
            tone="muted"
            testid="tracker-stat-cancelled"
          />
        </CardContent>
      </Card>

      {/* Commitment fidelity card. FP3-111B — hidden entirely when
          the viewer has Service Fidelity Monitoring OFF. Attendance
          stats above and any other tracker rows remain visible. */}
      {!isFidelityOff && (
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid="tracker-fidelity-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font">
            Service commitment fidelity · this month
          </CardTitle>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Direct service progress against target. Absent / unavailable minutes shown separately.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {fidelityRows.length === 0 ? (
            <p className="text-xs text-[#9B9890] italic">
              No direct-service commitments for this student.
            </p>
          ) : (
            fidelityRows.map((r) => {
              const tone =
                r.pct >= 95 ? "ok" : r.pct >= 70 ? "warn" : "low";
              const barClass =
                tone === "ok"
                  ? "bg-[#3F7A5C]"
                  : tone === "warn"
                  ? "bg-[#D9A05B]"
                  : "bg-[#B85C5C]";
              return (
                <div
                  key={r.key}
                  data-testid={`tracker-row-${r.key}`}
                  className="border border-[#E5E3DB] rounded-lg px-3 py-2.5"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <p className="text-sm font-semibold text-[#1F2937] capitalize">
                        {r.label}
                        {r.delivery && (
                          <span className="text-[10px] font-medium text-[#9B9890] ml-1.5">
                            · {r.delivery}
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-[#6B7280]">
                        Target {r.targetTotal} min/{r.period} ·{" "}
                        Scheduled {r.totalScheduledWeeklyMin} min/wk
                      </p>
                    </div>
                    <span
                      className={`text-xs font-bold tabular-nums ${
                        tone === "ok"
                          ? "text-[#3F7A5C]"
                          : tone === "warn"
                          ? "text-[#D9A05B]"
                          : "text-[#B85C5C]"
                      }`}
                    >
                      {r.pct}%
                    </span>
                  </div>
                  <div className="w-full bg-[#F3F1EC] rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-full ${barClass} transition-all`}
                      style={{ width: `${r.pct}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-[#6B7280]">
                    <span>
                      <span className="font-semibold text-[#1F2937]">{r.presentMin}</span> of{" "}
                      {r.periodTarget} min completed
                    </span>
                    {r.absentMin > 0 && (
                      <span className="text-[#B85C5C]">
                        · {r.absentMin} min absent
                      </span>
                    )}
                    {r.unavailMin > 0 && (
                      <span className="text-[#9C6B2E]">
                        · {r.unavailMin} min unavailable
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}

function TrackerStat({ label, value, tone, testid }) {
  const toneClass =
    tone === "ok"
      ? "bg-[#EFF3F1] text-[#3F7A5C] border-[#D6E4DA]"
      : tone === "warn"
      ? "bg-[#FBEFEC] text-[#B85C5C] border-[#F1D6D6]"
      : tone === "amber"
      // FP3-125 — Provider Unavailable — same amber palette used for
      // the Provider unavailable pill on Session History + Calendar.
      ? "bg-[#FDF8F3] text-[#9C6B2E] border-[#E9DAC0]"
      : "bg-[#F3F1EC] text-[#6B7280] border-[#E5E3DB]";
  return (
    <div data-testid={testid} className={`rounded-lg border px-3 py-2 ${toneClass}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
      <p className="text-2xl font-bold tabular-nums leading-none mt-1">{value}</p>
    </div>
  );
}
